import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { message, Spin, ColorPicker } from "antd";
import { ShoppingCartOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { useClient } from "../../context/ClientContext";
import { api } from "../../services/api";
import { BACKEND, resolveRad, resolveImgUrl, loadGoogleFont } from "../../templates/4/utils";
import { stripHtml } from "../../shared/components/RichContent";
import { T, Toggle, TInput, TArea, FieldLabel, Field, ColorChip, ColorRow, Select, SectionPlaceholder, TextStyleRow, WhatsAppNumberInput } from "./editorAtoms";

const GOOGLE_FONTS = [
  { label: "Sistema (default)", value: "system-ui, sans-serif" },
  { label: "Inter", value: "'Inter', sans-serif" },
  { label: "Roboto", value: "'Roboto', sans-serif" },
  { label: "Montserrat", value: "'Montserrat', sans-serif" },
  { label: "Poppins", value: "'Poppins', sans-serif" },
  { label: "Geist", value: "'Geist', sans-serif" },
  { label: "Playfair Display", value: "'Playfair Display', serif" },
  { label: "Raleway", value: "'Raleway', sans-serif" },
];

const HOME_SECTION_META = {
  hero: { label: "Mensaje de bienvenida", icon: "🏠" },
  carousel: { label: "Carrusel de imágenes", icon: "🖼" },
  featured_products: { label: "Productos destacados", icon: "⭐" },
  categories_banner: { label: "Banner de categorías", icon: "📂" },
  about: { label: "Quiénes Somos", icon: "💬" },
  shipping_info: { label: "Info de envíos", icon: "🚚" },
  new_products: { label: "Novedades / Ofertas", icon: "🆕" },
  contact: { label: "Contacto", icon: "📍" },
  brands_marquee: { label: "Marcas / Logos", icon: "🏷" },
  search: { label: "Buscador", icon: "🔍" },
};

const DEFAULT_CONFIG = {
  brand: {
    colors: {
      primary: "#4B72F5", accent: "#5BBEFC", background: "#f0f3ff",
      secondary: "#e8edfa", text: "#1a1a3a", primaryText: "#ffffff"
    },
    font: "system-ui, sans-serif",
    radius: "md",
  },
  header: {
    bgColor: "#ffffff", textColor: "#1a1a3a", iconColor: "#1a1a3a",
    logoPosition: "left", logoSize: "md", mobileCentered: false,
    announcement: { visible: false, text: "", bgColor: "#000000", textColor: "#ffffff", speed: 30 },
  },
  homeSections: [
    { type: "hero", visible: true, order: 0, slides: [{ title: "Bienvenidos", sub: "", cta: "Ver catálogo", image: null }], showArrows: true, arrowColor: "#ffffff" },
    { type: "carousel", visible: false, order: 1, images: [], showArrows: true, arrowColor: "#ffffff" },
    { type: "featured_products", visible: false, order: 2, title: "Productos destacados" },
    { type: "categories_banner", visible: false, order: 3, title: "", bgColor: "#ffffff", circleBg: "#f0f0f0", textPosition: "below" },
    { type: "about", visible: false, order: 4, title: "Quiénes Somos", body: "" },
    { type: "shipping_info", visible: false, order: 5, title: "Información de envíos", body: "" },
    { type: "new_products", visible: false, order: 6, title: "Novedades" },
    { type: "contact", visible: false, order: 7, title: "Contacto", address: "", phone: "", email: "" },
    { type: "brands_marquee", visible: false, order: 8, title: "Productos de las mejores marcas", titleAlign: "center", bgColor: "#ffffff", logos: [], speed: 30 },
    { type: "search", visible: false, order: 9, title: "¿Qué producto estás buscando?", heading: "", showCategoryChips: true },
  ],
  productList: { quickBuy: false, viewMode: "grid", showStock: false, showOutOfStock: false, bgColor: "" },
  productDetail: { showStock: true, showLastUnit: true, lastUnitMessage: "¡Último disponible, no te lo pierdas!" },
  cart: { minPurchase: 0, suggestProducts: false },
  footer: {
    bgColor: "#1a1a3a", textColor: "#ffffff",
    iconColor: "#5BBEFC", socialIconColor: "#ffffff",
    description: "",
    email: "", phone: "", address: "",
    showLogo: true, showNav: true, showPaymentMethods: true, showSocial: true,
  },
  floatingWhatsapp: { enabled: true },
  // Oculta hasta que el dueño la active: sin preguntas cargadas sería una
  // página vacía linkeada desde el pie.
  faqPage: {
    visible: false,
    title: "Preguntas frecuentes",
    subtitle: "",
    titleAlign: "center",
    bgColor: "#ffffff",
    showSearch: true,
    questionColor: "",
    answerColor: "",
  },
};


// ─── Section editors ──────────────────────────────────────────────────────────
// ─── Logo upload ──────────────────────────────────────────────────────────────
function LogoUploadSlot({ label, desc, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const imgUrl = resolveImgUrl(value);
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange(res.url);
      else message.error(res.error || "Error al subir imagen");
    } finally { setUploading(false); e.target.value = ""; }
  }
  return (
    <div style={{ marginBottom: 20 }}>
      <FieldLabel>{label}</FieldLabel>
      {desc && <div style={{ fontSize: 11, color: T.mut, marginBottom: 8, lineHeight: 1.5 }}>{desc}</div>}
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
          onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}
          style={{
            width: 140, height: 60, background: imgUrl ? "transparent" : "#f5f7ff",
            border: `1.5px dashed ${T.bdr}`, borderRadius: T.rs, overflow: "hidden",
            cursor: uploading ? "wait" : "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
            transition: "border-color 0.15s"
          }}>
          {uploading ? <span style={{ fontSize: 11, color: T.mut }}>Subiendo…</span>
            : imgUrl ? <img src={imgUrl} alt={label} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              : <span style={{ fontSize: 11, color: T.mut }}>Sin logo</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button onClick={() => inputRef.current?.click()}
            style={{
              fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueBg,
              border: `1px solid ${T.blue}`, borderRadius: 5, padding: "5px 12px", cursor: "pointer"
            }}>
            {imgUrl ? "Cambiar" : "Cargar"}
          </button>
        </div>
      </div>
    </div>
  );
}

const LOGO_SLOTS = [
  {
    key: "desktop", label: "Logo principal",
    desc: "Se muestra en el encabezado. Usá una versión sobre fondo claro."
  },
  {
    key: "mobile", label: "Logo en celulares",
    desc: "Versión compacta para pantallas chicas. Si no cargás uno, se usa el principal."
  },
  {
    key: "footer", label: "Logo en el pie de página",
    desc: "Generalmente la versión blanca o clara del logo, para fondos oscuros."
  },
  {
    key: "dark", label: "Logo para fondos oscuros (alternativo)",
    desc: "Se usa en secciones o elementos con fondo oscuro. Opcional."
  },
  {
    key: "login", label: "Logo en la pantalla de login",
    desc: "Se muestra en el formulario de inicio de sesión. Si no cargás uno, se usa el logo principal."
  },
];

function LogosEditor({ logos, onChange }) {
  const set = (k, v) => onChange({ ...logos, [k]: v });
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 20, lineHeight: 1.5 }}>
        Los logos se guardan junto con los colores al hacer clic en <strong>Guardar</strong>.
      </p>
      {LOGO_SLOTS.map(slot => (
        <LogoUploadSlot key={slot.key} label={slot.label} desc={slot.desc}
          value={logos[slot.key]} onChange={v => set(slot.key, v)} />
      ))}
    </div>
  );
}

function ColoresEditor({ brand, onChange }) {
  const c = brand.colors;
  const set = (k, v) => onChange({ ...brand, colors: { ...c, [k]: v } });
  return (
    <div style={{ padding: "0 2px" }}>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Estos colores se aplican en toda la tienda: botones, encabezado, fondos y textos.
      </p>
      <ColorRow label="Color principal" value={c.primary} onChange={v => set("primary", v)} />
      <ColorRow label="Color de acento" value={c.accent} onChange={v => set("accent", v)} />
      <ColorRow label="Fondo general" value={c.background} onChange={v => set("background", v)} />
      <ColorRow label="Fondo secundario" value={c.secondary} onChange={v => set("secondary", v)} />
      <ColorRow label="Texto" value={c.text} onChange={v => set("text", v)} />
      <ColorRow label="Texto sobre principal" value={c.primaryText} onChange={v => set("primaryText", v)} />
    </div>
  );
}

function TipoLetraEditor({ brand, onChange }) {
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        La tipografía se aplica en títulos, descripciones y botones de toda la tienda.
      </p>
      <Field label="Fuente">
        <Select value={brand.font} onChange={v => onChange({ ...brand, font: v })} options={GOOGLE_FONTS} />
      </Field>
      <div style={{ padding: "16px", background: T.sidebar, borderRadius: T.rs, border: `1px solid ${T.bdr}`, marginTop: 4 }}>
        <div style={{ fontFamily: brand.font, fontSize: 22, fontWeight: 700, color: T.txt, marginBottom: 6 }}>
          Título de ejemplo
        </div>
        <div style={{ fontFamily: brand.font, fontSize: 14, color: T.sub, lineHeight: 1.6 }}>
          Este es un texto de ejemplo con la fuente seleccionada para que puedas ver cómo se verá en tu tienda.
        </div>
        <button style={{
          marginTop: 12, padding: "8px 20px", background: brand.colors?.primary || "#4B72F5",
          color: brand.colors?.primaryText || "#fff", border: "none",
          borderRadius: resolveRad(brand.radius || "md"),
          fontFamily: brand.font, fontSize: 13, fontWeight: 600, cursor: "pointer"
        }}>
          Ver catálogo
        </button>
      </div>
      <Field label="Bordes (radio)" style={{ marginTop: 16 }}>
        <Select value={brand.radius || "md"} onChange={v => onChange({ ...brand, radius: v })} options={[
          { value: "none", label: "Sin borde (cuadrado)" },
          { value: "sm", label: "Suave" },
          { value: "md", label: "Redondeado (default)" },
          { value: "full", label: "Completamente redondo" },
        ]} />
      </Field>
    </div>
  );
}

function EncabezadoEditor({ header, onChange }) {
  const set = (k, v) => onChange({ ...header, [k]: v });
  const setA = (a) => onChange({ ...header, announcement: a });
  const annmt = header.announcement || {};
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Personalizá el navbar que aparece en la parte superior de todas las páginas.
      </p>
      <ColorRow label="Fondo del encabezado" value={header.bgColor} onChange={v => set("bgColor", v)} />
      <ColorRow label="Color de texto" value={header.textColor} onChange={v => set("textColor", v)} />
      <ColorRow label="Color de íconos" value={header.iconColor} onChange={v => set("iconColor", v)} />
      <div style={{ height: 16 }} />
      <Field label="Posición del logo">
        <Select value={header.logoPosition} onChange={v => set("logoPosition", v)} options={[
          { value: "left", label: "Izquierda" },
          { value: "center", label: "Centro" },
          { value: "right", label: "Derecha" },
        ]} />
      </Field>
      <Field label="Tamaño del logo">
        <Select value={header.logoSize} onChange={v => set("logoSize", v)} options={[
          { value: "sm", label: "Pequeño" },
          { value: "md", label: "Mediano" },
          { value: "lg", label: "Grande" },
        ]} />
      </Field>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Logo centrado en mobile</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>En pantallas chicas el logo se centra</div>
        </div>
        <Toggle on={header.mobileCentered} onChange={v => set("mobileCentered", v)} />
      </div>

      {/* Announcement bar */}
      <div style={{ marginTop: 4, paddingTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: annmt.visible ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Barra de anuncios</div>
            <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Ticker animado sobre el encabezado</div>
          </div>
          <Toggle on={annmt.visible} onChange={v => setA({ ...annmt, visible: v })} />
        </div>
        {annmt.visible && (
          <>
            <Field label="Texto del anuncio">
              <TInput value={annmt.text || ""} onChange={v => setA({ ...annmt, text: v })}
                placeholder="10% OFF con transferencia | Envíos a todo el país" />
            </Field>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <ColorRow label="Fondo" value={annmt.bgColor || "#000000"} onChange={v => setA({ ...annmt, bgColor: v })} />
              </div>
              <div style={{ flex: 1 }}>
                <ColorRow label="Texto" value={annmt.textColor || "#ffffff"} onChange={v => setA({ ...annmt, textColor: v })} />
              </div>
            </div>
            <Field label="Velocidad del desplazamiento">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: T.mut, flexShrink: 0 }}>Rápido</span>
                <input type="range" min={10} max={80} value={annmt.speed ?? 30}
                  onChange={e => setA({ ...annmt, speed: Number(e.target.value) })}
                  style={{ flex: 1, accentColor: T.blue }} />
                <span style={{ fontSize: 11, color: T.mut, flexShrink: 0 }}>Lento</span>
              </div>
            </Field>
          </>
        )}
      </div>
    </div>
  );
}

function HomeSectionRow({ section, onToggle, onEdit, isDragging, isDragOver }) {
  const meta = HOME_SECTION_META[section.type] || { label: section.type, icon: "📄" };
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff", borderRadius: T.rs, cursor: "pointer",
        border: `1.5px solid ${isDragOver ? T.blue : hov ? T.blue + "99" : T.bdr}`,
        marginBottom: 6, boxShadow: isDragOver ? `0 0 0 3px ${T.blueBg}` : hov ? `0 0 0 3px ${T.blueBg}` : T.sh,
        overflow: "hidden", opacity: isDragging ? 0.4 : 1,
        transition: "border-color 0.12s, box-shadow 0.12s",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>
        <span onClick={e => e.stopPropagation()}
          style={{
            fontSize: 14, color: T.mut, cursor: "grab", flexShrink: 0,
            letterSpacing: "-1px", userSelect: "none"
          }}>⠿</span>
        <span style={{ fontSize: 18, flexShrink: 0 }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: hov ? T.blue : T.txt, transition: "color 0.12s" }}>{meta.label}</div>
          <div style={{ fontSize: 11, color: section.visible ? T.ok : T.mut, marginTop: 1 }}>
            {section.visible ? "Visible" : "Oculta"}
          </div>
        </div>
        <Toggle on={section.visible} onChange={e => { onToggle(); }} />
      </div>
    </div>
  );
}

// ─── Editors por tipo de sección ─────────────────────────────────────────────
function SlideImageUpload({ image, onChange, uploading, onUpload, onRemove }) {
  const imgUrl = resolveImgUrl(image);
  return (
    <div>
      <FieldLabel>Imagen de fondo (opcional)</FieldLabel>
      <div onClick={() => !uploading && onUpload()}
        style={{
          height: 80, borderRadius: T.rs, border: `1.5px dashed ${T.bdr}`,
          background: imgUrl ? "transparent" : "#fafbfe", overflow: "hidden",
          cursor: uploading ? "wait" : "pointer", position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
        onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
        {uploading ? (
          <div style={{ textAlign: "center" }}><Spin size="small" />
            <div style={{ fontSize: 10, color: T.mut, marginTop: 3 }}>Subiendo...</div>
          </div>
        ) : imgUrl ? (
          <>
            <img src={imgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.15s"
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}>
              <span style={{ color: "#fff", fontSize: 11, fontWeight: 600 }}>Cambiar imagen</span>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>🖼</div>
            <div style={{ fontSize: 10, color: T.mut }}>Clic para subir imagen</div>
          </div>
        )}
      </div>
      {image && !uploading && (
        <button onClick={onRemove}
          style={{ marginTop: 3, fontSize: 11, color: T.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          × Quitar imagen
        </button>
      )}
    </div>
  );
}

function ImagePositionPicker({ image, positionY = 50, onChange }) {
  const imgUrl = resolveImgUrl(image);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const startRef = useRef(null);

  function handleMouseDown(e) {
    e.preventDefault();
    setDragging(true);
    startRef.current = { y: e.clientY, pos: positionY };
  }

  useEffect(() => {
    if (!dragging) return;
    function onMove(e) {
      const delta = e.clientY - startRef.current.y;
      const h = containerRef.current?.getBoundingClientRect().height || 1;
      const deltaPct = (delta / h) * 100;
      onChange(Math.round(Math.max(0, Math.min(100, startRef.current.pos + deltaPct))));
    }
    function onUp() { setDragging(false); }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, onChange]);

  return (
    <div style={{ marginTop: 10 }}>
      <FieldLabel>Posición de la imagen</FieldLabel>
      <div ref={containerRef}
        onMouseDown={handleMouseDown}
        style={{ height: 130, borderRadius: T.rs, overflow: "hidden", position: "relative",
          cursor: dragging ? "grabbing" : "grab", border: `1.5px solid ${dragging ? T.blue : T.bdr}`,
          userSelect: "none", transition: "border-color 0.15s" }}>
        <img src={imgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover",
          objectPosition: `center ${positionY}%`, display: "block", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: 0, right: 0, top: `${positionY}%`,
          transform: "translateY(-50%)", pointerEvents: "none" }}>
          <div style={{ height: 1.5, background: "rgba(255,255,255,0.7)",
            boxShadow: "0 0 5px rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, fontWeight: 700,
            padding: "2px 6px", borderRadius: 3 }}>{positionY}%</div>
        </div>
        {!dragging && (
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 9, fontWeight: 500,
            padding: "3px 9px", borderRadius: 4, whiteSpace: "nowrap", pointerEvents: "none" }}>
            ↕ Arrastrar para reposicionar
          </div>
        )}
      </div>
      <input type="range" min={0} max={100} value={positionY}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", marginTop: 6, accentColor: T.blue }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 9, color: T.mut }}>Arriba</span>
        <span style={{ fontSize: 9, color: T.mut }}>Centro</span>
        <span style={{ fontSize: 9, color: T.mut }}>Abajo</span>
      </div>
    </div>
  );
}

function CTAButtonEditor({ label, text, onText, config, onConfig, onRemove }) {
  const v = config?.variant || "outlined";
  const tc = config?.textColor || "#ffffff";
  const bc = config?.bgColor || "#ffffff";
  const set = (k, val) => onConfig({ ...(config || {}), [k]: val });
  const setColor = (cssKey, rawKey, raw, css) => onConfig({ ...(config || {}), [cssKey]: css, [rawKey]: raw });
  const isGradientBg = bc?.includes("gradient");
  const bgStyle = v === "filled"
    ? { background: bc, border: "none" }
    : isGradientBg
      ? { background: `linear-gradient(rgba(0,0,0,0),rgba(0,0,0,0)) padding-box, ${bc} border-box`, border: "2px solid transparent" }
      : { background: "transparent", border: `1.5px solid ${bc}` };
  const textStyle = tc?.includes("gradient")
    ? { background: tc, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }
    : { color: tc };
  return (
    <div style={{ border: `1px solid ${T.bdr}`, borderRadius: T.rs, padding: "12px", background: "#fafbfe" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <FieldLabel>{label}</FieldLabel>
        {onRemove && (
          <button onClick={onRemove}
            style={{ fontSize: 11, color: T.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            × Quitar
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <TInput value={text || ""} onChange={onText} placeholder="Ver catálogo" />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 4 }}>
            {[["outlined", "Con borde"], ["filled", "Relleno"]].map(([val, lbl]) => (
              <button key={val} onClick={() => set("variant", val)}
                style={{ fontSize: 11, padding: "5px 9px", borderRadius: 5, cursor: "pointer",
                  fontWeight: 600, fontFamily: "inherit",
                  border: `1.5px solid ${v === val ? T.blue : T.bdr}`,
                  background: v === val ? T.blueBg : "#fff",
                  color: v === val ? T.blue : T.sub }}>
                {lbl}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <ColorPicker size="small"
              value={(config?.textColorRaw && typeof config.textColorRaw.toHexString === "function" ? config.textColorRaw : null) ?? tc}
              mode={["single","gradient"]}
              onChange={(raw, css) => setColor("textColor", "textColorRaw", raw, css)} />
            <span style={{ fontSize: 8, color: T.mut }}>Texto</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <ColorPicker size="small"
              value={(config?.bgColorRaw && typeof config.bgColorRaw.toHexString === "function" ? config.bgColorRaw : null) ?? bc}
              mode={["single","gradient"]}
              onChange={(raw, css) => setColor("bgColor", "bgColorRaw", raw, css)} />
            <span style={{ fontSize: 8, color: T.mut }}>{v === "filled" ? "Fondo" : "Borde"}</span>
          </div>
          <div style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 5, fontSize: 11,
            fontWeight: 600, whiteSpace: "nowrap", ...bgStyle }}>
            <span style={textStyle}>{text || "Botón"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SlideEditor({ slide, slideIndex, total, onChange, onBack, onDelete }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => onChange({ ...slide, [k]: v });

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") set("image", res.url);
      else message.error(res.error || "Error al subir imagen");
    } catch { message.error("Error de conexión"); }
    finally { setUploading(false); }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <button onClick={onBack}
          style={{
            background: "none", border: "none", color: T.blue, fontSize: 12, fontWeight: 600,
            cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4
          }}>
          ← Volver
        </button>
        <span style={{ fontSize: 12, color: T.mut }}>Slide {slideIndex + 1} de {total}</span>
        {total > 1 && (
          <button onClick={onDelete}
            style={{
              marginLeft: "auto", fontSize: 11, color: T.danger, background: "none",
              border: `1px solid ${T.danger}`, borderRadius: 5, padding: "2px 8px", cursor: "pointer"
            }}>
            Eliminar slide
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SlideImageUpload
          image={slide.image}
          uploading={uploading}
          onUpload={() => fileRef.current?.click()}
          onRemove={() => { set("image", null); set("imagePositionY", undefined); }}
        />
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => handleFile(e.target.files[0])} />
        {slide.image && !uploading && (
          <ImagePositionPicker
            image={slide.image}
            positionY={slide.imagePositionY ?? 50}
            onChange={v => set("imagePositionY", v)}
          />
        )}
        <Field label="Título">
          <TInput value={slide.title || ""} onChange={v => set("title", v)} placeholder="Bienvenidos a nuestra tienda" />
          <TextStyleRow label="Título" style={slide.titleStyle} defaultColor="#ffffff"
            onChange={v => set("titleStyle", v)} />
        </Field>
        <Field label="Subtítulo (sobre el título)">
          <TInput value={slide.sub || ""} onChange={v => set("sub", v)} placeholder="DISTRIBUCIÓN MAYORISTA" />
          <TextStyleRow label="Sub" style={slide.subStyle} defaultColor="rgba(255,255,255,0.7)"
            onChange={v => set("subStyle", v)} />
        </Field>
        <Field label="Subtítulo (bajo el título)">
          <TInput value={slide.sub2 || ""} onChange={v => set("sub2", v)} placeholder="Texto secundario..." />
          <TextStyleRow label="Sub2" style={slide.sub2Style} defaultColor="rgba(255,255,255,0.7)"
            onChange={v => set("sub2Style", v)} />
        </Field>
        <CTAButtonEditor
          label="Botón principal (CTA)"
          text={slide.cta}
          onText={v => set("cta", v)}
          config={slide.ctaConfig}
          onConfig={v => set("ctaConfig", v)}
        />
        {slide.cta2 !== undefined ? (
          <CTAButtonEditor
            label="Botón secundario"
            text={slide.cta2}
            onText={v => set("cta2", v)}
            config={slide.cta2Config}
            onConfig={v => set("cta2Config", v)}
            onRemove={() => { set("cta2", undefined); set("cta2Config", undefined); }}
          />
        ) : (
          <button onClick={() => set("cta2", "")}
            style={{ width: "100%", padding: "8px", border: `1.5px dashed ${T.bdr}`,
              borderRadius: T.rs, background: "#fafbfe", color: T.blue, fontWeight: 600,
              fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            + Agregar segundo botón CTA
          </button>
        )}
      </div>
    </div>
  );
}

function HeroSectionEditor({ section, onChange }) {
  const [editingIdx, setEditingIdx] = useState(null);

  // Backward compat: normalizar al formato slides
  const slides = section.slides || [{ title: section.title || "", sub: section.sub || "", cta: section.cta || "", image: section.image || null }];

  function updateSlide(idx, updated) {
    const newSlides = slides.map((s, i) => i === idx ? updated : s);
    onChange({ ...section, slides: newSlides });
  }
  function addSlide() {
    onChange({ ...section, slides: [...slides, { title: "", sub: "", cta: "", image: null }] });
  }
  function deleteSlide(idx) {
    const newSlides = slides.filter((_, i) => i !== idx);
    onChange({ ...section, slides: newSlides });
    setEditingIdx(null);
  }

  if (editingIdx !== null) {
    return (
      <SlideEditor
        slide={slides[editingIdx]}
        slideIndex={editingIdx}
        total={slides.length}
        onChange={updated => updateSlide(editingIdx, updated)}
        onBack={() => setEditingIdx(null)}
        onDelete={() => deleteSlide(editingIdx)}
      />
    );
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 14, lineHeight: 1.5 }}>
        Podés tener múltiples slides en el mensaje de bienvenida. Cada uno puede tener imagen, texto y botón — todos opcionales.
      </p>
      {slides.map((slide, i) => {
        const imgUrl = resolveImgUrl(slide.image);
        return (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
            background: "#fff", borderRadius: T.rs, border: `1px solid ${T.bdr}`,
            marginBottom: 6, boxShadow: T.sh, cursor: "pointer"
          }}
            onClick={() => setEditingIdx(i)}>
            <div style={{
              width: 48, height: 34, borderRadius: 5, overflow: "hidden",
              background: imgUrl ? "transparent" : T.sidebar, flexShrink: 0,
              border: `1px solid ${T.bdr}`
            }}>
              {imgUrl && <img src={imgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: T.txt,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
              }}>
                {slide.title || <span style={{ color: T.mut, fontStyle: "italic" }}>Sin título</span>}
              </div>
              <div style={{ fontSize: 11, color: T.mut, marginTop: 1 }}>
                {slide.image ? "Con imagen" : "Sin imagen"}{slide.cta ? ` · Botón: ${slide.cta}` : ""}
              </div>
            </div>
            <span style={{ fontSize: 11, color: T.blue, fontWeight: 600 }}>Editar →</span>
          </div>
        );
      })}
      <button onClick={addSlide}
        style={{
          width: "100%", padding: "8px", border: `1.5px dashed ${T.bdr}`,
          borderRadius: T.rs, background: "#fafbfe", color: T.blue, fontWeight: 600,
          fontSize: 12, cursor: "pointer", marginTop: 4
        }}>
        + Agregar slide
      </button>
      {slides.length > 1 && (
        <div style={{
          marginTop: 16, padding: "12px 14px", background: T.sidebar,
          borderRadius: T.rs, border: `1px solid ${T.bdr}`
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.txt }}>Flechas de navegación</span>
            <Toggle on={section.showArrows !== false} onChange={v => onChange({ ...section, showArrows: v })} />
          </div>
          {section.showArrows !== false && (
            <ColorRow label="Color de flechas" value={section.arrowColor || "#ffffff"}
              onChange={v => onChange({ ...section, arrowColor: v })} />
          )}
        </div>
      )}
    </div>
  );
}

function CarouselSectionEditor({ section, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = section.images || [];

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange({ ...section, images: [...images, res.url] });
      else message.error(res.error || "Error al subir imagen");
    } catch { message.error("Error de conexión"); }
    finally { setUploading(false); }
  }

  function removeImage(idx) {
    onChange({ ...section, images: images.filter((_, i) => i !== idx) });
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 14, lineHeight: 1.5 }}>
        Agregá imágenes para el carrusel. Se muestran en orden, rotando automáticamente.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        {images.map((url, i) => {
          const src = resolveImgUrl(url);
          return (
            <div key={i} style={{ position: "relative", paddingBottom: "66%", borderRadius: T.rs, overflow: "hidden", background: "#eee" }}>
              <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
              <button onClick={() => removeImage(i)}
                style={{
                  position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%",
                  background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", cursor: "pointer",
                  fontSize: 13, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0
                }}>×</button>
            </div>
          );
        })}
        <div onClick={() => !uploading && fileRef.current?.click()}
          style={{
            paddingBottom: "66%", position: "relative", borderRadius: T.rs,
            border: `1.5px dashed ${T.bdr}`, background: "#fafbfe", cursor: uploading ? "wait" : "pointer"
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
          onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center"
          }}>
            {uploading ? <Spin size="small" /> : <><span style={{ fontSize: 20 }}>+</span><span style={{ fontSize: 10, color: T.mut }}>Agregar</span></>}
          </div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => handleFile(e.target.files[0])} />
      {images.length > 1 && (
        <div style={{
          marginTop: 12, padding: "12px 14px", background: T.sidebar,
          borderRadius: T.rs, border: `1px solid ${T.bdr}`
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.txt }}>Flechas de navegación</span>
            <Toggle on={section.showArrows !== false} onChange={v => onChange({ ...section, showArrows: v })} />
          </div>
          {section.showArrows !== false && (
            <ColorRow label="Color de flechas" value={section.arrowColor || "#ffffff"}
              onChange={v => onChange({ ...section, arrowColor: v })} />
          )}
        </div>
      )}
    </div>
  );
}

function CatImageUpload({ image, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const imgUrl = resolveImgUrl(image);
  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange(res.url);
      else message.error(res.error || "Error al subir imagen");
    } catch { message.error("Error de conexión"); }
    finally { setUploading(false); }
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div onClick={() => !uploading && fileRef.current?.click()}
        style={{
          width: 52, height: 52, borderRadius: T.rs, border: `1.5px dashed ${T.bdr}`,
          overflow: "hidden", cursor: uploading ? "wait" : "pointer", flexShrink: 0,
          background: imgUrl ? "transparent" : "#fafbfe",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
        onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
        <input type="file" accept="image/*" ref={fileRef} style={{ display: "none" }}
          onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) handleFile(f); }} />
        {uploading ? <Spin size="small" /> :
          imgUrl ? <img src={imgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
            <span style={{ color: T.mut, fontSize: 20 }}>+</span>}
      </div>
      {image && !uploading && (
        <button onClick={() => onChange(null)}
          style={{ fontSize: 11, color: T.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          × Quitar
        </button>
      )}
    </div>
  );
}

function CategoriesBannerEditor({ section, onChange }) {
  const [cats, setCats] = useState([]);
  const images = section.categoryImages || {};
  useEffect(() => {
    api.admin.catalogo.getCategorias()
      .then(res => { const all = Array.isArray(res) ? res : (res?.data || []); setCats(all.filter(c => !c.id_parent)); })
      .catch(() => { });
  }, []);
  const setImg = (id, url) => {
    const next = { ...images };
    if (url) next[id] = url; else delete next[id];
    onChange({ ...section, categoryImages: next });
  };
  const textPos = section.textPosition || "below";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <ColorRow label="Color de fondo de sección" value={section.bgColor || "#ffffff"}
        onChange={v => onChange({ ...section, bgColor: v })} />
      <ColorRow label="Color de fondo del círculo" value={section.circleBg || "#f0f0f0"}
        onChange={v => onChange({ ...section, circleBg: v })} />
      <div>
        <FieldLabel>Posición del texto</FieldLabel>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          {[["below", "Abajo"], ["above", "Arriba"]].map(([val, lbl]) => (
            <button key={val} onClick={() => onChange({ ...section, textPosition: val })}
              style={{
                flex: 1, padding: "7px 0", fontSize: 12, fontWeight: 600, borderRadius: T.rs, cursor: "pointer",
                border: `1.5px solid ${textPos === val ? T.blue : T.bdr}`,
                background: textPos === val ? T.blueBg : "#fff",
                color: textPos === val ? T.blue : T.txt
              }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <Field label="Estilo del nombre de categoría">
        <TextStyleRow label="Texto" style={section.textStyle} defaultColor="#1a1a2e"
          onChange={v => onChange({ ...section, textStyle: v })} />
      </Field>
      <div>
        <FieldLabel>Imagen por categoría</FieldLabel>
        {cats.length === 0
          ? <div style={{ fontSize: 12, color: T.mut, paddingTop: 8 }}>Cargando categorías...</div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            {cats.map(cat => (
              <div key={cat.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px", border: `1px solid ${T.bdr}`, borderRadius: T.rs
              }}>
                <CatImageUpload image={images[cat.id]} onChange={url => setImg(cat.id, url)} />
                <span style={{ fontSize: 13, fontWeight: 600, color: T.txt }}>{cat.nombre}</span>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function ShippingInfoEditor({ section, onChange }) {
  const DEFAULT_ITEMS = [
    { icon: "🚚", title: "Entrega coordinada", desc: "Coordinamos la logística para que tu pedido llegue sin demoras." },
    { icon: "📦", title: "Embalaje seguro", desc: "Todos los productos salen correctamente embalados." },
    { icon: "⚡", title: "Procesamiento rápido", desc: "Los pedidos se procesan el mismo día hábil." },
  ];
  const items = section.items || DEFAULT_ITEMS;
  const set = (k, v) => onChange({ ...section, [k]: v });
  const updateItem = (idx, field, val) =>
    onChange({ ...section, items: items.map((it, i) => i === idx ? { ...it, [field]: val } : it) });
  const addItem = () => onChange({ ...section, items: [...items, { icon: "📌", title: "", desc: "" }] });
  const removeItem = (idx) => onChange({ ...section, items: items.filter((_, i) => i !== idx) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título de la sección">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder="Información de envíos" />
        <TextStyleRow label="Título" style={section.titleStyle}
          onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
      <div>
        <FieldLabel>Cards de envío</FieldLabel>
        {items.map((item, idx) => (
          <div key={idx} style={{ border: `1px solid ${T.bdr}`, borderRadius: T.rs, padding: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-end" }}>
              <div style={{ flex: "0 0 64px" }}>
                <FieldLabel>Icono</FieldLabel>
                <TInput value={item.icon || ""} onChange={v => updateItem(idx, "icon", v)} placeholder="" />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Título</FieldLabel>
                <TInput value={item.title || ""} onChange={v => updateItem(idx, "title", v)} placeholder="Título..." />
              </div>
              {items.length > 1 && (
                <button onClick={() => removeItem(idx)}
                  style={{
                    padding: "6px 10px", background: "none", border: `1px solid ${T.bdr}`,
                    borderRadius: T.rs, color: T.danger, cursor: "pointer", fontSize: 12, flexShrink: 0
                  }}>✕</button>
              )}
            </div>
            <TArea value={item.desc || ""} onChange={v => updateItem(idx, "desc", v)} placeholder="Descripción breve..." rows={2} />
          </div>
        ))}
        {items.length < 5 && (
          <button onClick={addItem}
            style={{
              width: "100%", padding: 9, background: "none", border: `1.5px dashed ${T.bdr}`,
              borderRadius: T.rs, color: T.sub, fontSize: 13, cursor: "pointer", fontFamily: "inherit"
            }}>
            + Agregar card
          </button>
        )}
      </div>
    </div>
  );
}

function TextSectionEditor({ section, onChange, titlePlaceholder = "", bodyPlaceholder = "" }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título de la sección">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder={titlePlaceholder} />
        <TextStyleRow label="Título" style={section.titleStyle}
          onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
      <Field label="Texto">
        <TArea value={section.body || ""} onChange={v => set("body", v)} placeholder={bodyPlaceholder} rows={4} />
        <TextStyleRow label="Cuerpo" style={section.bodyStyle}
          onChange={v => set("bodyStyle", v)} />
      </Field>
    </div>
  );
}

function ContactSectionEditor({ section, onChange }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder="Encontranos" />
        <TextStyleRow label="Título" style={section.titleStyle}
          onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
      <Field label="Dirección">
        <TInput value={section.address || ""} onChange={v => set("address", v)} placeholder="Calle 123, Ciudad" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Teléfono">
          <TInput value={section.phone || ""} onChange={v => set("phone", v)} placeholder="(000) 000-0000" />
        </Field>
        <Field label="Email">
          <TInput value={section.email || ""} onChange={v => set("email", v)} placeholder="info@tienda.com" />
        </Field>
      </div>
      <div>
        <FieldLabel>Estilo info (email, teléfono, dirección)</FieldLabel>
        <TextStyleRow label="Info" style={section.infoStyle}
          onChange={v => set("infoStyle", v)} />
      </div>
    </div>
  );
}

function TitleAlignRow({ section, onChange }) {
  const align = section.titleAlign || "left";
  return (
    <Field label="Alineación del título">
      <div style={{ display: "flex", gap: 6 }}>
        {[["left", "Izquierda"], ["center", "Centro"]].map(([v, l]) => (
          <button key={v} onClick={() => onChange({ ...section, titleAlign: v })}
            style={{
              flex: 1, padding: "7px 0", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
              cursor: "pointer", borderRadius: T.rs,
              border: `1.5px solid ${align === v ? T.blue : T.bdr}`,
              background: align === v ? T.blueBg : "transparent",
              color: align === v ? T.blue : T.sub
            }}>
            {l}
          </button>
        ))}
      </div>
    </Field>
  );
}

function SimpleTitleEditor({ section, onChange, placeholder = "" }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título de la sección">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder={placeholder} />
        <TextStyleRow label="Título" style={section.titleStyle}
          onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
    </div>
  );
}


const ARROW_SIZES = [
  { value: "sm", label: "Chicas", px: "18px" },
  { value: "md", label: "Medianas", px: "24px" },
  { value: "lg", label: "Grandes", px: "32px" },
];

function FeaturedProductsEditor({ section, onChange }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  const showArrows = section.showArrows !== false;
  const arrowSize = section.arrowSize || "md";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título de la sección">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder="Productos destacados" />
        <TextStyleRow label="Título" style={section.titleStyle} onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
      <div style={{ borderTop: `1px solid ${T.bdr}`, paddingTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showArrows ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Flechas de navegación</div>
            <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Visibles cuando hay más productos que columnas</div>
          </div>
          <Toggle on={showArrows} onChange={v => set("showArrows", v)} />
        </div>
        {showArrows && (
          <>
            <ColorRow label="Color de flechas" value={section.arrowColor || "#1a1a3a"}
              onChange={v => set("arrowColor", v)} />
            <Field label="Tamaño de flechas">
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                {ARROW_SIZES.map(({ value, label }) => (
                  <button key={value} onClick={() => set("arrowSize", value)}
                    style={{
                      flex: 1, padding: "7px 0", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
                      cursor: "pointer", borderRadius: T.rs,
                      border: `1.5px solid ${arrowSize === value ? T.blue : T.bdr}`,
                      background: arrowSize === value ? T.blueBg : "transparent",
                      color: arrowSize === value ? T.blue : T.sub,
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          </>
        )}
      </div>
    </div>
  );
}

function BrandsMarqueeEditor({ section, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const logos = section.logos || [];
  const set = (k, v) => onChange({ ...section, [k]: v });

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange({ ...section, logos: [...logos, { id: Date.now(), url: res.url, alt: "" }] });
      else message.error(res.error || "Error al subir imagen");
    } catch { message.error("Error de conexión"); }
    finally { setUploading(false); }
  }

  function removeLogo(id) {
    onChange({ ...section, logos: logos.filter(l => l.id !== id) });
  }

  const speed = section.speed ?? 30;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Título de la sección">
        <TInput value={section.title || ""} onChange={v => set("title", v)} placeholder="Productos de las mejores marcas" />
        <TextStyleRow label="Título" style={section.titleStyle} onChange={v => set("titleStyle", v)} />
      </Field>
      <TitleAlignRow section={section} onChange={onChange} />
      <ColorRow label="Color de fondo" value={section.bgColor || "#ffffff"} onChange={v => set("bgColor", v)} />
      <div>
        <FieldLabel>Logos de marcas</FieldLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
          {logos.map(logo => {
            const src = resolveImgUrl(logo.url);
            return (
              <div key={logo.id} style={{ position: "relative" }}>
                <div style={{ width: 80, height: 48, borderRadius: T.rs, border: `1px solid ${T.bdr}`, overflow: "hidden", background: "#fafbfe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={src} alt="" style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }} />
                </div>
                <button onClick={() => removeLogo(logo.id)}
                  style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", background: T.danger, color: "#fff", border: "none", cursor: "pointer", fontSize: 11, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
              </div>
            );
          })}
          <div onClick={() => !uploading && fileRef.current?.click()}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}
            style={{ width: 80, height: 48, borderRadius: T.rs, border: `1.5px dashed ${T.bdr}`, background: "#fafbfe", cursor: uploading ? "wait" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, transition: "border-color 0.15s" }}>
            {uploading ? <Spin size="small" /> : <>
              <span style={{ fontSize: 18, color: T.mut }}>+</span>
              <span style={{ fontSize: 9, color: T.mut }}>Agregar</span>
            </>}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) handleFile(f); }} />
        <div style={{ fontSize: 11, color: T.mut, marginTop: 6, lineHeight: 1.5 }}>
          Los logos se van a mostrar en escala de grises y en loop continuo.
        </div>
      </div>
      <Field label="Velocidad del carrusel">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: T.mut, flexShrink: 0 }}>Rápido</span>
          <input type="range" min={10} max={80} value={speed}
            onChange={e => set("speed", Number(e.target.value))}
            style={{ flex: 1, accentColor: T.blue }} />
          <span style={{ fontSize: 11, color: T.mut, flexShrink: 0 }}>Lento</span>
        </div>
      </Field>
    </div>
  );
}

function FaviconUploadSlot({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const imgUrl = resolveImgUrl(value);
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange(res.url);
      else message.error(res.error || "Error al subir imagen");
    } finally { setUploading(false); e.target.value = ""; }
  }
  return (
    <div style={{ marginBottom: 20 }}>
      <FieldLabel>Favicon</FieldLabel>
      <div style={{ fontSize: 11, color: T.mut, marginBottom: 8, lineHeight: 1.5 }}>
        Ícono que aparece en la pestaña del navegador. Usá una imagen cuadrada (PNG o SVG, mínimo 32×32 px).
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
          onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}
          style={{
            width: 52, height: 52, background: imgUrl ? "transparent" : "#f5f7ff",
            border: `1.5px dashed ${T.bdr}`, borderRadius: T.rs, overflow: "hidden",
            cursor: uploading ? "wait" : "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
            transition: "border-color 0.15s"
          }}>
          {uploading ? <span style={{ fontSize: 10, color: T.mut }}>…</span>
            : imgUrl ? <img src={imgUrl} alt="favicon" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              : <span style={{ fontSize: 20, color: T.mut }}>🌐</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button onClick={() => inputRef.current?.click()}
            style={{
              fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueBg,
              border: `1px solid ${T.blue}`, borderRadius: 5, padding: "5px 12px", cursor: "pointer"
            }}>
            {imgUrl ? "Cambiar" : "Cargar"}
          </button>
          {value && (
            <button onClick={() => onChange("")}
              style={{ fontSize: 11, color: T.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              × Quitar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginBgUploadSlot({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const imgUrl = resolveImgUrl(value);
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onChange(res.url);
      else message.error(res.error || "Error al subir imagen");
    } finally { setUploading(false); e.target.value = ""; }
  }
  return (
    <div style={{ marginBottom: 20 }}>
      <FieldLabel>Imagen de fondo del login</FieldLabel>
      <div style={{ fontSize: 11, color: T.mut, marginBottom: 8, lineHeight: 1.5 }}>
        Se muestra detrás del formulario de inicio de sesión. Recomendamos una imagen de al menos 1200×800 px.
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
      <div onClick={() => !uploading && inputRef.current?.click()}
        onMouseEnter={e => e.currentTarget.style.borderColor = T.blue}
        onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}
        style={{
          height: 110, background: imgUrl ? "transparent" : "#f5f7ff",
          border: `1.5px dashed ${T.bdr}`, borderRadius: T.rs, overflow: "hidden",
          cursor: uploading ? "wait" : "pointer", display: "flex",
          alignItems: "center", justifyContent: "center", position: "relative",
          transition: "border-color 0.15s", marginBottom: 6
        }}>
        {uploading ? (
          <div style={{ textAlign: "center" }}>
            <Spin size="small" />
            <div style={{ fontSize: 10, color: T.mut, marginTop: 4 }}>Subiendo…</div>
          </div>
        ) : imgUrl ? (
          <>
            <img src={imgUrl} alt="fondo login" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.15s"
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}>
              <span style={{ color: "#fff", fontSize: 11, fontWeight: 600 }}>Cambiar imagen</span>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 26, marginBottom: 4 }}>🖼</div>
            <div style={{ fontSize: 11, color: T.mut }}>Clic para subir imagen</div>
          </div>
        )}
      </div>
      {value && !uploading && (
        <button onClick={() => onChange("")}
          style={{ fontSize: 11, color: T.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          × Quitar imagen
        </button>
      )}
    </div>
  );
}

function LoginEditor({ favicon, loginBg, onFaviconChange, onLoginBgChange }) {
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 20, lineHeight: 1.5 }}>
        Personalizá la pantalla de inicio de sesión de tu tienda.
      </p>
      <FaviconUploadSlot value={favicon} onChange={onFaviconChange} />
      <div style={{ height: 1, background: T.bdr, margin: "4px 0 20px" }} />
      <LoginBgUploadSlot value={loginBg} onChange={onLoginBgChange} />
    </div>
  );
}

function SearchSectionEditor({ section, onChange }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 2, lineHeight: 1.5 }}>
        Mostrá un buscador en la página de inicio. Al tipear se muestran resultados de productos y categorías en tiempo real.
      </p>
      <Field label="Placeholder del input">
        <TInput value={section.title || ""} onChange={v => set("title", v)}
          placeholder="¿Qué producto estás buscando?" />
      </Field>
      <Field label="Encabezado de sección (opcional)">
        <TInput value={section.heading || ""} onChange={v => set("heading", v)}
          placeholder="Buscá lo que necesitás" />
      </Field>
      <ColorRow label="Color de fondo" value={section.bgColor || "#fff9f5"}
        onChange={v => set("bgColor", v)} />
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Chips de categorías</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Muestra las primeras categorías como acceso rápido</div>
        </div>
        <Toggle on={section.showCategoryChips !== false} onChange={v => set("showCategoryChips", v)} />
      </div>
    </div>
  );
}

function renderSectionContentEditor(section, updateSection) {
  switch (section.type) {
    case "hero":
      return <HeroSectionEditor section={section} onChange={updateSection} />;
    case "carousel":
      return <CarouselSectionEditor section={section} onChange={updateSection} />;
    case "about":
      return <TextSectionEditor section={section} onChange={updateSection}
        titlePlaceholder="Quiénes Somos" bodyPlaceholder="Contá quiénes son, qué ofrecen y qué los diferencia..." />;
    case "shipping_info":
      return <ShippingInfoEditor section={section} onChange={updateSection} />;
    case "contact":
      return <ContactSectionEditor section={section} onChange={updateSection} />;
    case "featured_products":
      return <FeaturedProductsEditor section={section} onChange={updateSection} />;
    case "categories_banner":
      return <CategoriesBannerEditor section={section} onChange={updateSection} />;
    case "new_products":
      return <SimpleTitleEditor section={section} onChange={updateSection} placeholder="Novedades / Ofertas" />;
    case "brands_marquee":
      return <BrandsMarqueeEditor section={section} onChange={updateSection} />;
    case "search":
      return <SearchSectionEditor section={section} onChange={updateSection} />;
    default:
      return <p style={{ fontSize: 13, color: T.mut }}>Sin opciones de edición para esta sección.</p>;
  }
}

function PaginaInicioEditor({ sections, onChange, editingType, setEditingType }) {
  const [dragSrcType, setDragSrcType] = useState(null);
  const [dragOverType, setDragOverType] = useState(null);
  const dragTypeRef = useRef(null);

  const sorted = [...sections].sort((a, b) => a.order - b.order);

  function toggle(type) {
    onChange(sections.map(s => s.type === type ? { ...s, visible: !s.visible } : s));
  }
  function updateSection(updated) {
    onChange(sections.map(s => s.type === updated.type ? updated : s));
  }

  // Drag handlers
  function handleDragStart(type) {
    dragTypeRef.current = type;
    setDragSrcType(type);
  }
  function handleDragOver(e, type) {
    e.preventDefault();
    if (type !== dragTypeRef.current) setDragOverType(type);
  }
  function handleDrop(targetType) {
    const srcType = dragTypeRef.current;
    if (!srcType || srcType === targetType) return;
    const srcOrder = sections.find(s => s.type === srcType).order;
    const targetOrder = sections.find(s => s.type === targetType).order;
    onChange(sections.map(s => {
      if (s.type === srcType) return { ...s, order: targetOrder };
      if (s.type === targetType) return { ...s, order: srcOrder };
      return s;
    }));
  }
  function handleDragEnd() {
    dragTypeRef.current = null;
    setDragSrcType(null);
    setDragOverType(null);
  }

  // Vista de edición de una sección específica
  if (editingType) {
    const section = sections.find(s => s.type === editingType);
    const meta = HOME_SECTION_META[editingType] || { label: editingType, icon: "📄" };
    return (
      <div>
        <button onClick={() => setEditingType(null)}
          style={{
            display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            color: T.blue, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "0 0 14px", marginBottom: 4
          }}>
          ← Volver a secciones
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 20 }}>{meta.icon}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.txt }}>{meta.label}</span>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "8px 12px",
          background: section?.visible ? T.okBg : "#f0f2f8", borderRadius: T.rs,
          border: `1px solid ${section?.visible ? "rgba(5,150,105,0.25)" : T.bdr}`
        }}>
          <Toggle on={!!section?.visible} onChange={() => toggle(editingType)} />
          <span style={{ fontSize: 12, fontWeight: 600, color: section?.visible ? T.ok : T.mut }}>
            {section?.visible ? "Sección visible" : "Sección oculta en la tienda"}
          </span>
        </div>
        <div style={{ borderTop: `1px solid ${T.bdr}`, paddingTop: 16 }}>
          {section && renderSectionContentEditor(section, updateSection)}
        </div>
      </div>
    );
  }

  // Vista de lista con drag & drop
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 14, lineHeight: 1.5 }}>
        Arrastrá las secciones para reordenarlas. Activálas y editá su contenido.
      </p>
      {sorted.map((section) => (
        <div
          key={section.type}
          draggable
          onDragStart={() => handleDragStart(section.type)}
          onDragOver={e => handleDragOver(e, section.type)}
          onDrop={() => handleDrop(section.type)}
          onDragEnd={handleDragEnd}
          style={{ cursor: "grab" }}
        >
          <HomeSectionRow
            section={section}
            onToggle={() => toggle(section.type)}
            onEdit={() => setEditingType(section.type)}
            isDragging={dragSrcType === section.type}
            isDragOver={dragOverType === section.type}
          />
        </div>
      ))}
    </div>
  );
}

function ListadoEditor({ productList, onChange }) {
  const set = (k, v) => onChange({ ...productList, [k]: v });
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Configurá cómo se muestra el listado de productos en tu catálogo.
      </p>
      <Field label="Vista por defecto">
        <div style={{ display: "flex", gap: 8 }}>
          {[{ v: "grid", label: "Cuadrícula", icon: "⊞" }, { v: "list", label: "Lista", icon: "☰" }].map(({ v, label, icon }) => (
            <button key={v} onClick={() => set("viewMode", v)}
              style={{
                flex: 1, padding: "10px 0", border: `1.5px solid ${productList.viewMode === v ? T.blue : T.bdr}`,
                borderRadius: T.rs, background: productList.viewMode === v ? T.blueBg : "#fafbfe",
                color: productList.viewMode === v ? T.blue : T.sub,
                fontSize: 13, fontWeight: productList.viewMode === v ? 700 : 400, cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4
              }}>
              <span style={{ fontSize: 20 }}>{icon}</span>{label}
            </button>
          ))}
        </div>
      </Field>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Compra rápida</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Botón "agregar al carrito" desde el listado</div>
        </div>
        <Toggle on={productList.quickBuy} onChange={v => set("quickBuy", v)} />
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar stock disponible</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Cantidad disponible bajo cada producto</div>
        </div>
        <Toggle on={productList.showStock} onChange={v => set("showStock", v)} />
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar productos sin stock</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Se muestran con badge "Sin stock" y sin precio ni botón de compra</div>
        </div>
        <Toggle on={productList.showOutOfStock} onChange={v => set("showOutOfStock", v)} />
      </div>
      <Field label="Color de fondo del listado">
        <ColorRow>
          <ColorChip label="Fondo" value={productList.bgColor || ""} onChange={v => set("bgColor", v)}
            placeholder="Por defecto del tema" />
        </ColorRow>
      </Field>
    </div>
  );
}

function DetalleEditor({ productDetail, onChange }) {
  const set = (k, v) => onChange({ ...productDetail, [k]: v });
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Configurá la vista de detalle de cada producto.
      </p>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar stock disponible</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Ej: "3 unidades disponibles"</div>
        </div>
        <Toggle on={productDetail.showStock} onChange={v => set("showStock", v)} />
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Alerta última unidad</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Mensaje especial cuando queda 1 unidad</div>
        </div>
        <Toggle on={productDetail.showLastUnit} onChange={v => set("showLastUnit", v)} />
      </div>
      {productDetail.showLastUnit && (
        <div style={{ marginTop: 14 }}>
          <Field label="Texto del mensaje de última unidad">
            <TInput value={productDetail.lastUnitMessage}
              onChange={v => set("lastUnitMessage", v)}
              placeholder="¡Último disponible, no te lo pierdas!" />
          </Field>
          <div style={{
            padding: "10px 14px", background: "#fef3c7", borderRadius: T.rs,
            border: "1px solid #f59e0b", fontSize: 12, color: "#92400e", lineHeight: 1.5
          }}>
            {productDetail.lastUnitMessage || "(texto vacío)"}
          </div>
        </div>
      )}
    </div>
  );
}

function CarritoEditor({ cart, onChange }) {
  const set = (k, v) => onChange({ ...cart, [k]: v });
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Ajustá el comportamiento del carrito de compras.
      </p>
      <Field label="Monto mínimo de compra ($)">
        <TInput type="number" value={String(cart.minPurchase)}
          onChange={v => set("minPurchase", parseFloat(v) || 0)}
          placeholder="0 = sin mínimo" />
      </Field>
      {cart.minPurchase > 0 && (
        <div style={{
          padding: "10px 14px", background: T.blueBg, borderRadius: T.rs,
          border: `1px solid rgba(37,88,217,0.2)`, fontSize: 12, color: T.blue,
          lineHeight: 1.5, marginBottom: 14
        }}>
          Los clientes deberán alcanzar un mínimo de ${cart.minPurchase} para hacer el pedido.
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0"
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Sugerir productos</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Mostrar productos relacionados en el carrito</div>
        </div>
        <Toggle on={cart.suggestProducts} onChange={v => set("suggestProducts", v)} />
      </div>
    </div>
  );
}

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: "bi bi-instagram", placeholder: "https://instagram.com/tutienda" },
  { key: "facebook", label: "Facebook", icon: "bi bi-facebook", placeholder: "https://facebook.com/tutienda" },
  { key: "tiktok", label: "TikTok", icon: "bi bi-tiktok", placeholder: "https://tiktok.com/@tutienda" },
  { key: "twitter", label: "X (Twitter)", icon: "bi bi-twitter-x", placeholder: "https://x.com/tutienda" },
];

function PieEditor({ footer, onChange }) {
  const set = (k, v) => onChange({ ...footer, [k]: v });
  const desc = footer.description || "";
  const MAX_DESC = 160;
  const showSocial = footer.showSocial !== false;
  return (
    <div>
      <ColorRow label="Fondo del pie" value={footer.bgColor} onChange={v => set("bgColor", v)} />
      <ColorRow label="Color de texto" value={footer.textColor} onChange={v => set("textColor", v)} />
      <ColorRow label="Color de íconos de contacto" value={footer.iconColor || "#5BBEFC"} onChange={v => set("iconColor", v)} />
      <ColorRow label="Color de íconos de redes sociales" value={footer.socialIconColor || "#ffffff"} onChange={v => set("socialIconColor", v)} />

      <div style={{ height: 16 }} />

      <Field label="Descripción breve">
        <TArea value={desc} rows={3} placeholder="Breve descripción de tu empresa…"
          onChange={v => set("description", v.slice(0, MAX_DESC))} />
        <div style={{ fontSize: 10, color: desc.length >= MAX_DESC ? T.danger : T.mut, textAlign: "right", marginTop: 3 }}>
          {desc.length}/{MAX_DESC}
        </div>
      </Field>

      <FieldLabel>Contacto en el pie</FieldLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        <TInput value={footer.email || ""} onChange={v => set("email", v)} placeholder="Email" />
        <TInput value={footer.phone || ""} onChange={v => set("phone", v)} placeholder="Teléfono" />
        <TInput value={footer.address || ""} onChange={v => set("address", v)} placeholder="Dirección" />
      </div>

      {[
        { k: "showLogo", label: "Mostrar logo", desc: "Logo de la tienda en el pie" },
        { k: "showNav", label: "Mostrar navegación", desc: "Links a Inicio, Productos, etc." },
        { k: "showPaymentMethods", label: "Mostrar medios de pago", desc: "MercadoPago, transferencia, etc." },
        { k: "showSocial", label: "Mostrar redes sociales", desc: "Instagram, Facebook, TikTok, X" },
      ].map(({ k, label, desc: d }) => (
        <div key={k} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
        }}>
          <div>
            <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>{d}</div>
          </div>
          <Toggle on={footer[k] !== false} onChange={v => set(k, v)} />
        </div>
      ))}

      {showSocial && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {SOCIAL_PLATFORMS?.map(({ key, label, icon, placeholder }) => {
             if (!key) return null;
            const capitalize = (str = "") =>
              str.charAt(0).toUpperCase() + str.slice(1);

            const showKey = `show${capitalize(key)}`;
            const isOn = footer[showKey] !== false;
            return (
              <div key={key} style={{ border: `1px solid ${T.bdr}`, borderRadius: T.rs, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isOn ? 8 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={icon} style={{ fontSize: 15, color: T.sub }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.txt }}>{label}</span>
                  </div>
                  <Toggle on={isOn} onChange={v => set(showKey, v)} />
                </div>
                {isOn && (
                  <TInput value={footer[key] || ""} onChange={v => set(key, v)}
                    placeholder={placeholder} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WidgetsEditor({ data, onChange, whatsappNumber, onChangeNumber }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const enabled = data.enabled !== false;
  const hasNumber = !!whatsappNumber;
  return (
    <div>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 18, lineHeight: 1.5 }}>
        Botón flotante de acceso rápido a WhatsApp, visible en todas las páginas de la tienda.
      </p>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar botón flotante</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Aparece abajo a la derecha en toda la tienda</div>
        </div>
        <Toggle on={enabled} onChange={v => set("enabled", v)} />
      </div>

      <Field label="Número de WhatsApp">
        <WhatsAppNumberInput value={whatsappNumber} onChange={onChangeNumber} />
      </Field>

      {!hasNumber && (
        <div style={{
          marginTop: 4, padding: "10px 14px", background: "#fff7ed", borderRadius: T.rs,
          border: "1px solid rgba(234,88,12,0.25)", fontSize: 12, color: "#c2410c", lineHeight: 1.5,
        }}>
          Cargá tu número para que el botón se muestre en la tienda.
        </div>
      )}
    </div>
  );
}

// ─── Preview sub-componentes con estado propio ────────────────────────────────
function PreviewArrowBtn({ side, color, onClick, scale = 1 }) {
  const d = side === "left"
    ? "M15 3L3 12L15 21"
    : "M3 3L15 12L3 21";
  const w = Math.round(18 * scale);
  const h = Math.round(24 * scale);
  return (
    <button onClick={e => { e.stopPropagation(); onClick?.(); }}
      style={{
        position: "absolute", [side]: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3,
        background: "none", border: "none", padding: 0, cursor: "pointer",
        filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.6))", lineHeight: 0
      }}>
      <svg width={w} height={h} viewBox="0 0 18 24" fill="none">
        <path d={d} stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function PreviewHeroSlider({ section, c, rad, isMobile = false }) {
  const slides = section.slides || [{ title: section.title, sub: section.sub, cta: section.cta, image: section.image }];
  const [idx, setIdx] = useState(0);
  const cur = slides[Math.min(idx, slides.length - 1)] || {};
  const heroImg = resolveImgUrl(cur.image);
  const arrowColor = section.arrowColor || "#ffffff";
  const prev = () => setIdx(i => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx(i => (i + 1) % slides.length);
  return (
    <div style={{
      position: "relative", height: isMobile ? 160 : 220, overflow: "hidden",
      background: heroImg ? "#0a0a0a" : `linear-gradient(130deg,${c.primary},${c.accent})`
    }}>
      {heroImg && <img src={heroImg} alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 1,
          objectPosition: `center ${cur.imagePositionY ?? 50}%` }} />}
      <div style={{ position: "relative", zIndex: 1, padding: isMobile ? "20px 14px" : "32px 20px", maxWidth: isMobile ? "100%" : "65%" }}>
        {cur.sub && (() => {
          const col = cur.subStyle?.color || "rgba(255,255,255,0.7)";
          const isGrad = col.includes("gradient");
          return <div style={{
            fontSize: pfs(cur.subStyle, 10),
            fontFamily: cur.subStyle?.fontFamily || "inherit",
            letterSpacing: 2, textTransform: "uppercase", marginBottom: 6,
            ...(isGrad ? { background: col, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" } : { color: col })
          }}>{cur.sub}</div>;
        })()}
        {cur.title && (() => {
          const col = cur.titleStyle?.color || "#fff";
          const isGrad = col.includes("gradient");
          return <div style={{
            fontSize: pfs(cur.titleStyle, 20),
            fontFamily: cur.titleStyle?.fontFamily || "inherit",
            fontWeight: 800, marginBottom: 6, lineHeight: 1.1,
            ...(isGrad ? { background: col, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" } : { color: col })
          }}>{cur.title}</div>;
        })()}
        {cur.sub2 && (() => {
          const col = cur.sub2Style?.color || "rgba(255,255,255,0.7)";
          const isGrad = col.includes("gradient");
          return <div style={{
            fontSize: pfs(cur.sub2Style, 10),
            fontFamily: cur.sub2Style?.fontFamily || "inherit",
            marginBottom: 10, lineHeight: 1.4,
            ...(isGrad ? { background: col, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" } : { color: col })
          }}>{cur.sub2}</div>;
        })()}
        {(cur.cta || cur.cta2 !== undefined) && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {cur.cta && (() => {
              const cfg = cur.ctaConfig || {};
              const variant = cfg.variant || "outlined";
              const tc = cfg.textColor || "#fff";
              const bc = cfg.bgColor;
              const isGradBg = bc?.includes("gradient");
              const isGradTc = tc?.includes("gradient");
              const bg = variant === "filled"
                ? { background: bc || "#ffffff", border: "none" }
                : isGradBg
                  ? { background: `linear-gradient(rgba(0,0,0,0),rgba(0,0,0,0)) padding-box, ${bc} border-box`, border: "2px solid transparent" }
                  : { background: "rgba(255,255,255,0.15)", border: `1.5px solid ${bc || "rgba(255,255,255,0.5)"}` };
              const txt = isGradTc
                ? { background: tc, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }
                : { color: tc };
              return (
                <div style={{ display: "inline-block", borderRadius: rad, padding: "6px 14px", fontSize: 11, fontWeight: 600, ...bg }}>
                  <span style={txt}>{cur.cta}</span>
                </div>
              );
            })()}
            {cur.cta2 !== undefined && cur.cta2 !== "" && (() => {
              const cfg = cur.cta2Config || {};
              const variant = cfg.variant || "outlined";
              const tc = cfg.textColor || "#fff";
              const bc = cfg.bgColor;
              const isGradBg = bc?.includes("gradient");
              const isGradTc = tc?.includes("gradient");
              const bg = variant === "filled"
                ? { background: bc || "#ffffff", border: "none" }
                : isGradBg
                  ? { background: `linear-gradient(rgba(0,0,0,0),rgba(0,0,0,0)) padding-box, ${bc} border-box`, border: "2px solid transparent" }
                  : { background: "rgba(255,255,255,0.1)", border: `1.5px solid ${bc || "rgba(255,255,255,0.4)"}` };
              const txt = isGradTc
                ? { background: tc, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }
                : { color: tc };
              return (
                <div style={{ display: "inline-block", borderRadius: rad, padding: "6px 14px", fontSize: 11, fontWeight: 600, ...bg }}>
                  <span style={txt}>{cur.cta2}</span>
                </div>
              );
            })()}
          </div>
        )}
      </div>
      {slides.length > 1 && (
        <>
          {section.showArrows !== false && <>
            <PreviewArrowBtn side="left" color={arrowColor} onClick={prev} />
            <PreviewArrowBtn side="right" color={arrowColor} onClick={next} />
          </>}
          <div style={{
            position: "absolute", bottom: 8, left: 0, right: 0,
            display: "flex", justifyContent: "center", gap: 5, zIndex: 2
          }}>
            {slides.map((_, si) => (
              <div key={si} onClick={() => setIdx(si)}
                style={{
                  width: si === idx ? 16 : 6, height: 6, borderRadius: 3, cursor: "pointer", transition: "width 0.2s",
                  background: si === idx ? "#fff" : "rgba(255,255,255,0.45)"
                }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PreviewCarouselSlider({ section, c, isMobile = false }) {
  const imgs = section.images || [];
  const [idx, setIdx] = useState(0);
  const arrowColor = section.arrowColor || "#ffffff";
  const prev = () => setIdx(i => (i - 1 + imgs.length) % imgs.length);
  const next = () => setIdx(i => (i + 1) % imgs.length);
  if (imgs.length === 0) return (
    <div style={{
      background: c.secondary, height: isMobile ? 120 : 160, display: "flex",
      alignItems: "center", justifyContent: "center", color: c.text, fontSize: 12, opacity: .6
    }}>
      🖼 Carrusel — agregá imágenes en "Editar contenido"
    </div>
  );
  return (
    <div style={{ position: "relative", height: isMobile ? 130 : 220, overflow: "hidden" }}>
      <img src={resolveImgUrl(imgs[idx])} alt=""
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      {imgs.length > 1 && (
        <>
          {section.showArrows !== false && <>
            <PreviewArrowBtn side="left" color={arrowColor} onClick={prev} />
            <PreviewArrowBtn side="right" color={arrowColor} onClick={next} />
          </>}
          <div style={{
            position: "absolute", bottom: 8, left: 0, right: 0,
            display: "flex", justifyContent: "center", gap: 5, zIndex: 2
          }}>
            {imgs.map((_, ii) => (
              <div key={ii} onClick={() => setIdx(ii)}
                style={{
                  width: ii === idx ? 16 : 6, height: 6, borderRadius: 3, cursor: "pointer", transition: "width 0.2s",
                  background: ii === idx ? "#fff" : "rgba(255,255,255,0.45)"
                }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Preview section overlay ──────────────────────────────────────────────────
function PreviewSectionWrapper({ type, editingType, onEdit, dragSrc, dragOver, onDragStart, onDragOver, onDrop, onDragEnd, children }) {
  const [hovered, setHovered] = useState(false);
  const isEditing = editingType === type;
  const isDragging = dragSrc === type;
  const isOver = dragOver === type && dragSrc !== type;
  return (
    <div
      style={{
        position: "relative",
        outline: isEditing ? "2px solid #2558d9" : isOver ? "2px dashed #2558d988" : hovered ? "2px solid #2558d940" : "none",
        outlineOffset: -2,
        opacity: isDragging ? 0.35 : 1,
        transition: "opacity 0.15s",
        cursor: "grab",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      draggable
      onDragStart={e => { e.stopPropagation(); onDragStart(type); }}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOver(type); }}
      onDrop={e => { e.stopPropagation(); onDrop(type); }}
      onDragEnd={onDragEnd}
    >
      {(hovered || isEditing) && (
        <div style={{ position: "absolute", top: 6, right: 6, zIndex: 20, display: "flex", gap: 4 }}>
          <div title="Arrastrar para reordenar"
            style={{
              background: "rgba(0,0,0,0.5)", borderRadius: 5, padding: "4px 6px",
              cursor: "grab", display: "flex", alignItems: "center"
            }}>
            <svg width="10" height="14" viewBox="0 0 10 14" fill="white">
              <circle cx="3" cy="2.5" r="1.5" /><circle cx="7" cy="2.5" r="1.5" />
              <circle cx="3" cy="7" r="1.5" /><circle cx="7" cy="7" r="1.5" />
              <circle cx="3" cy="11.5" r="1.5" /><circle cx="7" cy="11.5" r="1.5" />
            </svg>
          </div>
          <button
            onClick={e => { e.stopPropagation(); e.preventDefault(); onEdit(); }}
            style={{
              background: isEditing ? "#2558d9" : "rgba(0,0,0,0.5)", borderRadius: 5,
              padding: "4px 10px", color: "#fff", fontSize: 11, fontWeight: 600,
              border: "none", cursor: "pointer"
            }}>
            {isEditing ? "✓ Editando" : "Editar"}
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Catalog preview (used when activeSection === "listing") ──────────────────
function PreviewCatalogPage({ products, c, rad, pl }) {
  const { quickBuy, viewMode, showStock } = pl;
  const displayProds = products.length > 0 ? products.slice(0, 8) : Array(8).fill(null);

  // Grid card — identical structure to featured_products/new_products home cards
  function GridCard({ p }) {
    const hasPrice = p?.precio != null && p?.precio > 0;
    return (
      <div style={{
        background: "#fff", borderRadius: 8, border: `1px solid rgba(0,0,0,0.08)`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column"
      }}>
        <div style={{
          height: 60, background: "#f5f5f5", borderRadius: "7px 7px 0 0", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
        }}>
          {p?.imagen
            ? <img src={resolveImgUrl(p.imagen)} alt={p.nombre}
              style={{ width: "78%", height: "78%", objectFit: "contain" }} />
            : <div style={{ width: "100%", height: "100%", background: "#f0f0f0" }} />
          }
        </div>
        <div style={{ padding: "5px 6px 6px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            {p?.codigo && (
              <div style={{ fontSize: 7, color: "#bbb", marginBottom: 2, letterSpacing: "0.02em" }}>CÓD. {p.codigo}</div>
            )}
            <div style={{ height: 14, overflow: "hidden", marginBottom: 2 }}>
              {p
                ? <div style={{
                  fontSize: 8, fontWeight: 600, color: c.text, lineHeight: 1.4,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                }}>{p.nombre}</div>
                : <><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "80%", marginBottom: 3 }} /><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "55%" }} /></>
              }
            </div>
            <div style={{ height: 11, overflow: "hidden" }}>
              {hasPrice
                ? <div style={{ fontSize: 9, fontWeight: 700, color: c.primary, lineHeight: "11px" }}>${p.precio.toFixed(2)}</div>
                : <div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "40%", marginTop: 3 }} />
              }
            </div>
            {showStock && p && (
              <div style={{ fontSize: 7, color: "#999", marginTop: 3 }}>
                {p.stock > 0 ? `${p.stock} disp.` : "Sin stock"}
              </div>
            )}
          </div>
          {quickBuy && (
            <div style={{ display: "flex", gap: 3, marginTop: 5 }}>
              <div style={{
                flex: 1, height: 16, background: c.primary, borderRadius: rad,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 2
              }}>
                <ShoppingCartOutlined style={{ fontSize: 6, color: c.primaryText }} />
                <span style={{ fontSize: 6, fontWeight: 600, color: c.primaryText }}>Agregar al carrito</span>
              </div>
              <div style={{
                width: 16, height: 16, background: c.primary, borderRadius: rad, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <EyeOutlined style={{ fontSize: 8, color: c.primaryText }} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List row — inspired by marketplace-style listing
  function ListItem({ p }) {
    const hasPrice = p?.precio != null && p?.precio > 0;
    return (
      <div style={{
        background: "#fff", borderRadius: 8, border: `1px solid rgba(0,0,0,0.08)`,
        display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
      }}>
        <div style={{
          width: 52, height: 52, flexShrink: 0, background: "#f5f5f5", borderRadius: rad,
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
        }}>
          {p?.imagen
            ? <img src={resolveImgUrl(p.imagen)} alt={p.nombre}
              style={{ width: "80%", height: "80%", objectFit: "contain" }} />
            : <div style={{ width: "100%", height: "100%", background: "#f0f0f0" }} />
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {p?.codigo && (
            <div style={{ fontSize: 7, color: "#bbb", marginBottom: 1 }}>CÓD. {p.codigo}</div>
          )}
          {p
            ? <div style={{
              fontSize: 9, fontWeight: 600, color: c.text, lineHeight: 1.4,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
            }}>{p.nombre}</div>
            : <><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "70%", marginBottom: 3 }} /><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "45%" }} /></>
          }
          {showStock && p && (
            <div style={{ fontSize: 7, color: "#999", marginTop: 2 }}>
              {p.stock > 0 ? `${p.stock} disponibles` : "Sin stock"}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: hasPrice ? c.primary : "#ccc" }}>
            {hasPrice ? `$${p.precio.toFixed(2)}` : <div style={{ height: 6, background: "#e8e8e8", borderRadius: 2, width: 40 }} />}
          </div>
          {quickBuy && (
            <div style={{ display: "flex", gap: 3 }}>
              <div style={{
                height: 18, padding: "0 6px", background: c.primary, borderRadius: rad,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 2
              }}>
                <ShoppingCartOutlined style={{ fontSize: 7, color: c.primaryText }} />
                <span style={{ fontSize: 6.5, fontWeight: 600, color: c.primaryText }}>Agregar</span>
              </div>
              <div style={{
                width: 18, height: 18, background: c.primary,
                borderRadius: rad, display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <EyeOutlined style={{ fontSize: 8, color: c.primaryText }} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const pageBg = pl.bgColor || c.background;

  return (
    <div style={{ background: pageBg, padding: "14px 16px" }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 3, height: 18, background: c.primary, borderRadius: 2 }} />
          <span style={{ fontSize: 13, fontWeight: 800, color: c.text }}>Productos</span>
        </div>
        <div style={{
          height: 22, background: "#fff", border: `1px solid rgba(0,0,0,0.12)`,
          borderRadius: rad, display: "flex", alignItems: "center", padding: "0 8px", gap: 4, minWidth: 110
        }}>
          <SearchOutlined style={{ fontSize: 9, color: "#bbb" }} />
          <span style={{ fontSize: 9, color: "#ccc" }}>Buscar...</span>
        </div>
      </div>

      {viewMode === "list" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {displayProds.map((p, i) => <ListItem key={p?.id ?? i} p={p} />)}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {displayProds.map((p, i) => <GridCard key={p?.id ?? i} p={p} />)}
        </div>
      )}
    </div>
  );
}

// scale a user-specified fontSize to preview dimensions (preview is ~40% of real site width)
const pfs = (style, fallback) => style?.fontSize ? Math.round(style.fontSize * 0.4) : fallback;

// ─── Detail preview (used when activeSection === "detail") ────────────────────
function PreviewDetailPage({ products, c, rad, pd, isMobile }) {
  const p = products[0] || null;
  const associated = products.slice(1, 5);
  const imgSrc = p?.imagen ? resolveImgUrl(p.imagen) : null;
  const hasPrice = p?.precio != null && p?.precio > 0;

  const mockVariants = [
    { label: "Color", values: ["Negro", "Blanco"], selected: 0 },
    { label: "Talle", values: ["S", "M", "L"], selected: 1 },
  ];

  return (
    <div style={{ background: "#fff", padding: isMobile ? "8px 8px 16px" : "12px 12px 18px", fontFamily: "inherit" }}>
      {/* Main layout */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 12, alignItems: "flex-start" }}>

        {/* Gallery */}
        <div style={{ width: isMobile ? "100%" : "38%", flexShrink: 0 }}>
          {!isMobile ? (
            <div style={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
              {/* Thumbnails vertical */}
              <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 16, height: 16, borderRadius: 3, background: "#f0f0f0",
                    border: `1.5px solid ${i === 0 ? "#374151" : "#e5e7eb"}`,
                    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {i === 0 && imgSrc && <img src={imgSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
                  </div>
                ))}
              </div>
              {/* Main image */}
              <div style={{
                flex: 1, background: "#f5f5f7", borderRadius: Math.min(rad, 6),
                display: "flex", alignItems: "center", justifyContent: "center",
                height: 100, overflow: "hidden",
              }}>
                {imgSrc
                  ? <img src={imgSrc} alt="" style={{ maxWidth: "75%", maxHeight: "75%", objectFit: "contain" }} />
                  : <div style={{ width: "50%", height: "50%", background: "#e0e0e0", borderRadius: 3 }} />
                }
              </div>
            </div>
          ) : (
            <div>
              <div style={{ background: "#f5f5f7", borderRadius: Math.min(rad, 6), display: "flex", alignItems: "center", justifyContent: "center", height: 70 }}>
                {imgSrc
                  ? <img src={imgSrc} alt="" style={{ maxWidth: "65%", maxHeight: "65%", objectFit: "contain" }} />
                  : <div style={{ width: "45%", height: "45%", background: "#e0e0e0", borderRadius: 3 }} />
                }
              </div>
              <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 14, height: 14, borderRadius: 2, background: "#f0f0f0",
                    border: `1.5px solid ${i === 0 ? "#374151" : "#e5e7eb"}`,
                  }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Breadcrumb */}
          <div style={{ fontSize: 6, color: "#9ca3af", marginBottom: 3 }}>Categoría / Subcategoría</div>

          {/* Name */}
          <div style={{ fontSize: isMobile ? 9 : 10, fontWeight: 800, color: c.text, lineHeight: 1.2, marginBottom: 4 }}>
            {p?.nombre || "Nombre del producto"}
          </div>

          {/* Price */}
          {hasPrice && (
            <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 700, color: c.text, marginBottom: 5 }}>
              ${p.precio.toFixed(2)}
            </div>
          )}

          {/* Description */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 6, fontWeight: 700, color: "#374151", marginBottom: 2 }}>Descripción</div>
            <div style={{ fontSize: 6, color: "#6b7280", lineHeight: 1.5 }}>
              {p?.descripcion ? p.descripcion.slice(0, 60) + "…" : "Descripción del producto con características y detalles relevantes."}
            </div>
          </div>

          {/* Variants */}
          {mockVariants.map(prop => (
            <div key={prop.label} style={{ marginBottom: 5 }}>
              <div style={{ fontSize: 6, fontWeight: 700, color: "#374151", marginBottom: 2 }}>
                {prop.label} <span style={{ fontWeight: 400, color: "#6b7280" }}>{prop.values[prop.selected]}</span>
              </div>
              <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {prop.values.map((v, i) => (
                  <div key={v} style={{
                    padding: "1px 5px", borderRadius: Math.min(rad, 4),
                    border: `1px solid ${i === prop.selected ? c.primary : "#d1d5db"}`,
                    background: i === prop.selected ? `${c.primary}14` : "#fff",
                    color: i === prop.selected ? c.primary : "#374151",
                    fontSize: 6, fontWeight: i === prop.selected ? 700 : 400,
                  }}>
                    {v}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Last unit alert */}
          {pd.showLastUnit && (
            <div style={{ fontSize: 6, color: "#f59e0b", fontWeight: 600, marginBottom: 3 }}>
              {pd.lastUnitMessage || "¡Última unidad!"}
            </div>
          )}

          {/* Stock */}
          {pd.showStock && (
            <div style={{ fontSize: 6, color: "#6b7280", marginBottom: 4 }}>
              Stock disponible: {p?.stock ?? 12}
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: "#f3f4f6", margin: "3px 0 5px" }} />

          {/* Button */}
          <div style={{
            height: 16, borderRadius: Math.min(rad, 6),
            background: c.primary,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
          }}>
            <ShoppingCartOutlined style={{ fontSize: 6, color: c.primaryText }} />
            <span style={{ fontSize: 6, fontWeight: 600, color: c.primaryText }}>Agregar al carrito</span>
          </div>
        </div>
      </div>

      {/* Associated products */}
      {associated.length > 0 && (
        <div style={{ marginTop: isMobile ? 12 : 16 }}>
          <div style={{ fontSize: isMobile ? 8 : 9, fontWeight: 800, color: c.text, marginBottom: 8 }}>
            Tal vez te interese…
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(associated.length, isMobile ? 2 : 4)}, 1fr)`,
            gap: isMobile ? 5 : 8,
          }}>
            {associated.map((asoc, i) => {
              const aImg = asoc?.imagen ? resolveImgUrl(asoc.imagen) : null;
              const aHasPrice = asoc?.precio != null && asoc?.precio > 0;
              return (
                <div key={i} style={{ background: "#fff", borderRadius: Math.min(rad, 6), border: "1px solid #ebebeb", overflow: "hidden" }}>
                  <div style={{ background: "#f5f5f7", height: isMobile ? 36 : 44, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
                    {aImg
                      ? <img src={aImg} alt="" style={{ maxWidth: "75%", maxHeight: "75%", objectFit: "contain" }} />
                      : <div style={{ width: "45%", height: "45%", background: "#e0e0e0", borderRadius: 2 }} />
                    }
                  </div>
                  <div style={{ padding: "4px 5px 5px" }}>
                    <div style={{ fontSize: 6, fontWeight: 600, color: c.text, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {asoc?.nombre || "Producto"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      {aHasPrice && <span style={{ fontSize: 6, fontWeight: 700, color: c.text }}>${asoc.precio.toFixed(2)}</span>}
                      <div style={{
                        width: 12, height: 12, borderRadius: "50%",
                        border: `1px solid ${c.primary}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        marginLeft: "auto",
                      }}>
                        <ShoppingCartOutlined style={{ fontSize: 5, color: c.primary }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Live Preview ─────────────────────────────────────────────────────────────
// ─── FAQ preview (used when activeSection === "faq") ───────────────────────────
const previewNormKw = (s = "") => s.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function PreviewFaqPage({ faqPage, faqs, c, rad, isMobile }) {
  const page = faqPage || {};
  const align = page.titleAlign || "center";
  const questionColor = page.questionColor || c.text;
  const answerColor = page.answerColor || c.text;
  const showTags = page.showSearch !== false;
  const items = faqs.length > 0 ? faqs : [
    { pregunta: "¿Cuánto tarda el envío?", respuesta: "Entre 3 y 5 días hábiles.", icono: "🚚", keywords: "envio" },
    { pregunta: "¿Qué medios de pago aceptan?", respuesta: "Tarjeta, transferencia y efectivo.", icono: "💳", keywords: "pago" },
  ];
  const tags = (() => {
    const seen = new Map();
    items.forEach(it => (it.keywords || "").split(",").map(k => k.trim()).filter(Boolean).forEach(k => {
      const key = previewNormKw(k);
      if (!seen.has(key)) seen.set(key, k);
    }));
    return [...seen.values()];
  })();

  return (
    <div style={{ background: page.bgColor || "#ffffff", padding: isMobile ? "16px 12px 24px" : "22px 20px 32px" }}>
      <div style={{ maxWidth: isMobile ? "94%" : "68%", margin: "0 auto" }}>
        {page.title && (
          <div style={{
            fontWeight: 800, fontSize: pfs(page.titleStyle, isMobile ? 13 : 17),
            fontFamily: page.titleStyle?.fontFamily || "inherit",
            color: page.titleStyle?.color || c.text,
            textAlign: align, marginBottom: 4,
          }}>{page.title}</div>
        )}
        <div style={{ width: 18, height: 3, background: c.primary, borderRadius: 2,
          margin: align === "center" ? "0 auto 8px" : align === "right" ? "0 0 8px auto" : "0 0 8px" }} />
        {page.subtitle && (
          <div style={{ fontSize: 9, color: c.text, opacity: 0.65, textAlign: align, marginBottom: 14 }}>
            {page.subtitle}
          </div>
        )}
        {showTags && tags.length > 0 && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 14,
            justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
          }}>
            {["Todas", ...tags].map((t, i) => (
              <span key={t} style={{
                padding: "4px 10px", borderRadius: 999, fontSize: 8, fontWeight: 600,
                border: `1px solid ${i === 0 ? c.text : "rgba(0,0,0,0.15)"}`,
                background: i === 0 ? c.text : "transparent",
                color: i === 0 ? "#fff" : c.text,
              }}>{t}</span>
            ))}
          </div>
        )}
        <div>
          {items.slice(0, 6).map((item, idx) => (
            <div key={idx} style={{ borderBottom: idx === items.length - 1 ? "none" : "1px solid rgba(0,0,0,0.06)", padding: "9px 0", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                border: "1px solid rgba(0,0,0,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
              }}>{item.icono || "❓"}</div>
              <div style={{ flex: 1, fontWeight: 700, fontSize: 9, color: questionColor }}>{item.pregunta}</div>
              <div style={{ color: c.text, opacity: 0.5, fontSize: 8 }}>▾</div>
            </div>
          ))}
          {items[0]?.respuesta && (
            <div style={{ fontSize: 8, color: answerColor, opacity: page.answerColor ? 1 : 0.7, padding: "0 0 8px 28px", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {stripHtml(items[0].respuesta)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function T4Preview({ cfg, storeName, products = [], categorias = [], faqs = [], logos = {}, device = "desktop", editingType, onEditSection, onReorderSections, activeSection, whatsappNumber }) {
  const { brand, header, homeSections, footer, floatingWhatsapp } = cfg;
  const c = brand.colors;
  const isMobile = device === "mobile";
  const sorted = [...(homeSections || [])].sort((a, b) => a.order - b.order).filter(s => s.visible);
  const logoSizes = { sm: 24, md: 32, lg: 44 };
  const logoH = logoSizes[header.logoSize] || 32;
  const rad = resolveRad(brand?.radius);
  const previewProds = products.slice(0, 6);
  const navLogoSrc = device === "mobile" ? (logos.mobile || logos.desktop) : logos.desktop;
  const isListing = activeSection === "listing";
  const isDetail = activeSection === "detail";
  const isFaq = activeSection === "faq";

  const [pDragSrc, setPDragSrc] = useState(null);
  const pTickerRef = useRef(null);
  const pTickerDur = header.announcement?.speed ?? 30;
  const [pDragOver, setPDragOver] = useState(null);
  const pDragRef = useRef(null);
  function pHandleDragStart(type) { pDragRef.current = type; setPDragSrc(type); }
  function pHandleDragOver(type) { if (type !== pDragRef.current) setPDragOver(type); }
  function pHandleDrop(type) {
    const src = pDragRef.current;
    if (src && src !== type) onReorderSections?.(src, type);
  }
  function pHandleDragEnd() { pDragRef.current = null; setPDragSrc(null); setPDragOver(null); }

  return (
    <div style={{ fontFamily: brand.font, fontSize: 13, position: "relative" }}>
      <style>{`@keyframes t4-marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
      {floatingWhatsapp?.enabled !== false && whatsappNumber && (
        <div style={{
          position: "absolute", bottom: 14, right: 14, width: 38, height: 38, borderRadius: "50%",
          background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 3px 10px rgba(0,0,0,0.28)", zIndex: 20,
        }}>
          <i className="bi bi-whatsapp" style={{ color: "#fff", fontSize: 19 }} />
        </div>
      )}
      {/* Announcement bar */}
      {header.announcement?.visible && header.announcement?.text && (
        <div style={{
          background: header.announcement.bgColor || "#000",
          height: 28, overflow: "hidden", display: "flex", alignItems: "center"
        }}>
          <div ref={pTickerRef} style={{
            display: "flex",
            animation: `t4-marquee ${pTickerDur}s linear infinite`
          }}>
            {[0, 1].map(g => (
              <div key={g} aria-hidden={g > 0} style={{ display: "flex", flexShrink: 0 }}>
                {Array(20).fill(null).map((_, i) => (
                  <span key={i} style={{
                    paddingRight: 40, whiteSpace: "nowrap", fontSize: 10,
                    fontWeight: 500, color: header.announcement.textColor || "#fff", letterSpacing: "0.04em"
                  }}>
                    {header.announcement.text}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navbar */}
      <div style={{
        background: header.bgColor, padding: "8px 18px", display: "flex",
        alignItems: "center", gap: 10
      }}>
        {header.logoPosition === "left" && (
          navLogoSrc
            ? <img src={resolveImgUrl(navLogoSrc)} alt={storeName}
              style={{ height: logoH, maxWidth: logoH * 3, objectFit: "contain", flexShrink: 0 }} />
            : <div style={{ height: logoH, width: logoH * 2.5, background: c.secondary, borderRadius: rad, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, textAlign: header.logoPosition === "center" ? "center" : "left" }}>
          {header.logoPosition === "center" && (
            navLogoSrc
              ? <img src={resolveImgUrl(navLogoSrc)} alt={storeName}
                style={{ height: logoH, maxWidth: logoH * 3, objectFit: "contain" }} />
              : <div style={{ display: "inline-flex", height: logoH, width: logoH * 2.5, background: c.secondary, borderRadius: rad }} />
          )}
          {header.logoPosition !== "center" && !navLogoSrc && (
            <span style={{ color: header.textColor, fontWeight: 700, fontSize: 14 }}>{storeName}</span>
          )}
        </div>
        <ShoppingCartOutlined style={{ color: header.iconColor, fontSize: 16 }} />
        <div style={{
          width: 24, height: 24, borderRadius: "50%", background: c.primary,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 700, color: c.primaryText, flexShrink: 0
        }}>LT</div>
        {header.logoPosition === "right" && (
          navLogoSrc
            ? <img src={resolveImgUrl(navLogoSrc)} alt={storeName}
              style={{ height: logoH, maxWidth: logoH * 3, objectFit: "contain" }} />
            : <div style={{ height: logoH, width: logoH * 2.5, background: c.secondary, borderRadius: rad }} />
        )}
      </div>

      {/* Nav links row */}
      <div style={{
        background: header.bgColor, borderTop: `1px solid ${header.textColor}12`,
        display: "flex", justifyContent: "center", gap: 0, padding: "0 18px"
      }}>
        {["Inicio", "Productos", "Mis Pedidos"].map(l => (
          <span key={l} style={{
            padding: "7px 10px", fontSize: 9, fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase",
            color: header.textColor, opacity: 0.7, whiteSpace: "nowrap"
          }}>
            {l}
          </span>
        ))}
      </div>

      {/* Home sections / Catalog preview */}
      {isFaq
        ? <PreviewFaqPage faqPage={cfg.faqPage || {}} faqs={faqs} c={c} rad={rad} isMobile={isMobile} />
        : isDetail
        ? <PreviewDetailPage products={previewProds} c={c} rad={rad} pd={cfg.productDetail || {}} isMobile={isMobile} />
        : isListing
        ? <PreviewCatalogPage products={previewProds} c={c} rad={rad} pl={cfg.productList || {}} />
        : sorted.map((section, i) => {
          let content = null;
          if (section.type === "hero")
            content = <PreviewHeroSlider section={section} c={c} rad={rad} isMobile={isMobile} />;
          else if (section.type === "carousel")
            content = <PreviewCarouselSlider section={section} c={c} isMobile={isMobile} />;
          else if (section.type === "featured_products") {
            const fpCols = isMobile ? 2 : 4;
            const slots = Array(fpCols).fill(null).map((_, i) => previewProds[i] || null);
            const fpA = section.titleAlign || "left";
            const fpShowArrows = section.showArrows !== false && !isMobile;
            const fpArrowColor = section.arrowColor || "#1a1a3a";
            const fpArrowScale = section.arrowSize === "sm" ? 0.65 : section.arrowSize === "lg" ? 1.2 : 0.85;
            const fpImgH = isMobile ? 60 : 90;
            content = (
              <div style={{ padding: isMobile ? "20px 12px" : "28px 20px", background: c.background }}>
                <div style={{ maxWidth: "94%", margin: "0 auto" }}>
                  {section.title && (
                    <div style={{
                      fontWeight: 800,
                      fontSize: pfs(section.titleStyle, isMobile ? 12 : 15),
                      fontFamily: section.titleStyle?.fontFamily || "inherit",
                      color: section.titleStyle?.color || c.text,
                      textAlign: fpA, marginBottom: 4
                    }}>{section.title}</div>
                  )}
                  <div style={{
                    width: 24, height: 3, background: c.primary, borderRadius: 2,
                    margin: fpA === "center" ? "0 auto 12px" : "0 0 12px"
                  }} />
                  <div style={{ position: "relative" }}>
                  {fpShowArrows && <PreviewArrowBtn side="left" color={fpArrowColor} scale={fpArrowScale} />}
                  {fpShowArrows && <PreviewArrowBtn side="right" color={fpArrowColor} scale={fpArrowScale} />}
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${fpCols},1fr)`, gap: isMobile ? 6 : 8, padding: fpShowArrows ? "0 28px" : 0 }}>
                    {slots.map((p, n) => (
                      <div key={p?.id ?? n} style={{
                        background: "#fff", borderRadius: 8,
                        border: `1px solid rgba(0,0,0,0.08)`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        display: "flex", flexDirection: "column"
                      }}>
                        <div style={{
                          height: fpImgH, background: "#f5f5f5", borderRadius: "7px 7px 0 0", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
                        }}>
                          {p?.imagen
                            ? <img src={resolveImgUrl(p.imagen)} alt={p.nombre}
                              style={{ width: "78%", height: "78%", objectFit: "contain" }} />
                            : <div style={{ width: "100%", height: "100%", background: "#f0f0f0" }} />
                          }
                        </div>
                        <div style={{ padding: "5px 6px 6px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ height: 14, overflow: "hidden", marginBottom: 2 }}>
                              {p
                                ? <div style={{
                                  fontSize: 8, fontWeight: 600, color: c.text, lineHeight: 1.4,
                                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                                }}>{p.nombre}</div>
                                : <><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "80%", marginBottom: 3 }} /><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "55%" }} /></>
                              }
                            </div>
                            <div style={{ height: 11, overflow: "hidden", marginBottom: 0 }}>
                              {p?.precio != null && p.precio > 0
                                ? <div style={{ fontSize: 9, fontWeight: 700, color: c.primary, lineHeight: "11px" }}>${p.precio.toFixed(2)}</div>
                                : <div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "40%", marginTop: 3 }} />
                              }
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 3, marginTop: 5 }}>
                            <div style={{
                              flex: 1, height: 16, background: c.primary, borderRadius: rad,
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 2
                            }}>
                              <ShoppingCartOutlined style={{ fontSize: 6, color: c.primaryText }} />
                              {!isMobile && <span style={{ fontSize: 6, fontWeight: 600, color: c.primaryText }}>Agregar al carrito</span>}
                            </div>
                            <div style={{
                              width: 16, height: 16, background: c.primary, borderRadius: rad, flexShrink: 0,
                              display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                              <EyeOutlined style={{ fontSize: 8, color: c.primaryText }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                  <div style={{ textAlign: "center", marginTop: 10 }}>
                    <div style={{
                      display: "inline-block", padding: "6px 18px", background: c.primary,
                      color: c.primaryText, borderRadius: rad, fontSize: 10, fontWeight: 600
                    }}>
                      Ver catálogo completo
                    </div>
                  </div>
                </div>
              </div>
            );
          } else if (section.type === "categories_banner") {
            const catList = categorias.length > 0
              ? categorias.filter(c => !c.id_parent).slice(0, isMobile ? 4 : 6)
              : [{ id: "a", nombre: "Camisas" }, { id: "b", nombre: "Lino" }, { id: "c", nombre: "Algodón" }];
            const catImgs = section.categoryImages || {};
            const pCircleBg = section.circleBg || "#f0f0f0";
            const pTextPos = section.textPosition || "below";
            const pts = section.textStyle || {};
            const ptsColor = pts.color;
            const ptsGrad = ptsColor?.includes("gradient");
            const pCircleSize = isMobile ? 36 : 48;
            const pLabelStyle = {
              fontSize: pfs(pts, isMobile ? 7 : 8),
              fontWeight: 600,
              fontFamily: pts.fontFamily || "inherit",
              textAlign: "center", lineHeight: 1.3,
              ...(ptsGrad
                ? { background: ptsColor, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }
                : { color: ptsColor || c.text }),
            };
            content = (
              <div style={{ padding: isMobile ? "14px 12px" : "18px 20px", background: section.bgColor || "#ffffff" }}>
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: isMobile ? 8 : 12 }}>
                  {catList.map(cat => {
                    const imgUrl = catImgs[cat.id] ? resolveImgUrl(catImgs[cat.id]) : null;
                    return (
                      <div key={cat.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: pCircleSize + 8, flexShrink: 0 }}>
                        {pTextPos === "above" && <div style={pLabelStyle}>{cat.nombre}</div>}
                        <div style={{
                          width: pCircleSize, height: pCircleSize, borderRadius: "50%", background: pCircleBg, overflow: "hidden",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          border: `2px solid ${c.primary}`, boxShadow: `0 3px 10px ${c.primary}30`
                        }}>
                          {imgUrl
                            ? <img src={imgUrl} alt={cat.nombre} style={{ width: "78%", height: "78%", objectFit: "contain" }} />
                            : <div style={{ fontSize: isMobile ? 12 : 16, fontWeight: 700, color: c.primary, opacity: .5 }}>
                              {cat.nombre[0]?.toUpperCase()}
                            </div>
                          }
                        </div>
                        {pTextPos !== "above" && <div style={pLabelStyle}>{cat.nombre}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          } else if (section.type === "about") {
            const abA = section.titleAlign || "left";
            content = (
              <div style={{ padding: isMobile ? "20px 12px" : "28px 20px", background: c.background }}>
                <div style={{ maxWidth: "94%", margin: "0 auto" }}>
                  <div style={{
                    fontWeight: 700,
                    fontSize: pfs(section.titleStyle, isMobile ? 12 : 14),
                    fontFamily: section.titleStyle?.fontFamily || "inherit",
                    color: section.titleStyle?.color || c.text,
                    textAlign: abA, marginBottom: 4
                  }}>{section.title}</div>
                  <div style={{
                    width: 24, height: 3, background: c.primary, borderRadius: 2,
                    margin: abA === "center" ? "0 auto 8px" : "0 0 8px"
                  }} />
                  <div style={{
                    fontSize: pfs(section.bodyStyle, isMobile ? 10 : 12),
                    fontFamily: section.bodyStyle?.fontFamily || "inherit",
                    color: section.bodyStyle?.color || c.text,
                    opacity: section.bodyStyle?.color ? 1 : .7, lineHeight: 1.6
                  }}>{section.body || "Texto sobre la empresa."}</div>
                </div>
              </div>
            );
          } else if (section.type === "shipping_info") {
            const siA = section.titleAlign || "left";
            const shipItems = section.items || [
              { icon: "🚚", title: "Entrega coordinada", desc: "Coordinamos la logística para que tu pedido llegue." },
              { icon: "📦", title: "Embalaje seguro", desc: "Todos los productos salen correctamente embalados." },
              { icon: "⚡", title: "Procesamiento rápido", desc: "Los pedidos se procesan el mismo día hábil." },
            ];
            content = (
              <div style={{ padding: isMobile ? "20px 12px" : "28px 20px", background: c.secondary }}>
                <div style={{ maxWidth: "94%", margin: "0 auto" }}>
                  {section.title && (
                    <div style={{
                      fontWeight: 700,
                      fontSize: pfs(section.titleStyle, isMobile ? 11 : 13),
                      fontFamily: section.titleStyle?.fontFamily || "inherit",
                      color: section.titleStyle?.color || c.text,
                      textAlign: siA, marginBottom: 4
                    }}>{section.title}</div>
                  )}
                  <div style={{
                    width: 20, height: 3, background: c.primary, borderRadius: 2,
                    margin: siA === "center" ? "0 auto 10px" : "0 0 10px"
                  }} />
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${shipItems.length},1fr)`, gap: isMobile ? 6 : 8 }}>
                    {shipItems.map((item, ii) => (
                      <div key={ii} style={{
                        background: "#fff", borderRadius: 8, padding: isMobile ? "8px" : "10px",
                        display: "flex", gap: 8, alignItems: "flex-start", border: `1px solid rgba(0,0,0,0.06)`
                      }}>
                        <span style={{ fontSize: isMobile ? 14 : 16, flexShrink: 0 }}>{item.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: isMobile ? 9 : 10, color: c.text, marginBottom: 2 }}>{item.title}</div>
                          <div style={{ fontSize: isMobile ? 8 : 9, color: c.text, opacity: .65, lineHeight: 1.4 }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          } else if (section.type === "new_products") {
            const npA = section.titleAlign || "left";
            const npCols = isMobile ? 2 : 4;
            const npImgH = isMobile ? 60 : 90;
            const npSlots = previewProds.length > 0 ? previewProds.slice(0, npCols) : Array(npCols).fill(null);
            content = (
              <div style={{ padding: isMobile ? "20px 12px" : "28px 20px", background: c.background }}>
                <div style={{ maxWidth: "94%", margin: "0 auto" }}>
                  <div style={{
                    fontWeight: 800,
                    fontSize: pfs(section.titleStyle, isMobile ? 12 : 14),
                    fontFamily: section.titleStyle?.fontFamily || "inherit",
                    color: section.titleStyle?.color || c.text,
                    textAlign: npA, marginBottom: 4
                  }}>{section.title}</div>
                  <div style={{
                    width: 20, height: 3, background: c.primary, borderRadius: 2,
                    margin: npA === "center" ? "0 auto 10px" : "0 0 10px"
                  }} />
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${npCols},1fr)`, gap: isMobile ? 6 : 8 }}>
                    {npSlots.map((p, n) => (
                      <div key={p?.id ?? n} style={{
                        background: "#fff", borderRadius: 8,
                        border: `1px solid rgba(0,0,0,0.08)`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        display: "flex", flexDirection: "column"
                      }}>
                        <div style={{
                          height: npImgH, background: "#f5f5f5", borderRadius: "7px 7px 0 0", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
                        }}>
                          {p?.imagen
                            ? <img src={resolveImgUrl(p.imagen)} alt={p.nombre}
                              style={{ width: "78%", height: "78%", objectFit: "contain" }} />
                            : <div style={{ width: "100%", height: "100%", background: "#f0f0f0" }} />
                          }
                        </div>
                        <div style={{ padding: "5px 6px 6px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ height: 14, overflow: "hidden", marginBottom: 2 }}>
                              {p
                                ? <div style={{
                                  fontSize: 8, fontWeight: 600, color: c.text, lineHeight: 1.4,
                                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                                }}>{p.nombre}</div>
                                : <><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "80%", marginBottom: 3 }} /><div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "55%" }} /></>
                              }
                            </div>
                            <div style={{ height: 11, overflow: "hidden" }}>
                              {p?.precio != null && p.precio > 0
                                ? <div style={{ fontSize: 9, fontWeight: 700, color: c.primary, lineHeight: "11px" }}>${p.precio.toFixed(2)}</div>
                                : <div style={{ height: 5, background: "#e8e8e8", borderRadius: 2, width: "40%", marginTop: 3 }} />
                              }
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 3, marginTop: 5 }}>
                            <div style={{
                              flex: 1, height: 16, background: c.primary, borderRadius: rad,
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 2
                            }}>
                              <ShoppingCartOutlined style={{ fontSize: 6, color: c.primaryText }} />
                              {!isMobile && <span style={{ fontSize: 6, fontWeight: 600, color: c.primaryText }}>Agregar al carrito</span>}
                            </div>
                            <div style={{
                              width: 16, height: 16, background: c.primary, borderRadius: rad, flexShrink: 0,
                              display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                              <EyeOutlined style={{ fontSize: 8, color: c.primaryText }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          } else if (section.type === "contact") {
            const ctA = isMobile ? "center" : (section.titleAlign || "left");
            content = (
              <div style={{ padding: isMobile ? "20px 12px" : "28px 20px", background: c.secondary }}>
                <div style={{ maxWidth: "94%", margin: "0 auto" }}>
                  <div style={{
                    fontWeight: 800,
                    fontSize: pfs(section.titleStyle, isMobile ? 12 : 14),
                    fontFamily: section.titleStyle?.fontFamily || "inherit",
                    color: section.titleStyle?.color || c.text,
                    textAlign: ctA, marginBottom: 4
                  }}>{section.title}</div>
                  <div style={{
                    width: 24, height: 3, background: c.primary, borderRadius: 2,
                    margin: ctA === "center" ? "0 auto 10px" : "0 0 10px"
                  }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {section.email && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                        justifyContent: isMobile ? "center" : "flex-start",
                        fontSize: pfs(section.infoStyle, isMobile ? 10 : 12),
                        fontFamily: section.infoStyle?.fontFamily || "inherit",
                        color: section.infoStyle?.color || c.text, opacity: .8 }}>
                        <i className="bi bi-envelope" style={{ color: c.primary, fontSize: isMobile ? 11 : 14 }} />
                        <span>{section.email}</span>
                      </div>
                    )}
                    {section.phone && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                        justifyContent: isMobile ? "center" : "flex-start",
                        fontSize: pfs(section.infoStyle, isMobile ? 10 : 12),
                        fontFamily: section.infoStyle?.fontFamily || "inherit",
                        color: section.infoStyle?.color || c.text, opacity: .8 }}>
                        <i className="bi bi-telephone" style={{ color: c.primary, fontSize: isMobile ? 11 : 14 }} />
                        <span>{section.phone}</span>
                      </div>
                    )}
                    {section.address && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                        justifyContent: isMobile ? "center" : "flex-start",
                        fontSize: pfs(section.infoStyle, isMobile ? 10 : 12),
                        fontFamily: section.infoStyle?.fontFamily || "inherit",
                        color: section.infoStyle?.color || c.text, opacity: .8 }}>
                        <i className="bi bi-geo-alt" style={{ color: c.primary, fontSize: isMobile ? 11 : 14 }} />
                        <span>{section.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (section.type === "brands_marquee") {
            const bmA = section.titleAlign || "center";
            const bmLogos = section.logos || [];
            const bmBg = section.bgColor || "#ffffff";
            const bmSpeed = section.speed ?? 30;
            const bmItems = bmLogos.length > 0
              ? bmLogos
              : [{ id: "a" }, { id: "b" }, { id: "c" }, { id: "d" }, { id: "e" }];
            const repeated = [...bmItems, ...bmItems];
            content = (
              <div style={{ background: bmBg, padding: "14px 0" }}>
                {section.title && (
                  <div style={{
                    fontWeight: 700,
                    fontSize: pfs(section.titleStyle, 13),
                    fontFamily: section.titleStyle?.fontFamily || "inherit",
                    color: section.titleStyle?.color || c.text,
                    textAlign: bmA, marginBottom: 6, padding: "0 16px",
                  }}>{section.title}</div>
                )}
                {section.title && (
                  <div style={{
                    width: 24, height: 3, background: c.primary, borderRadius: 2, marginBottom: 10,
                    margin: bmA === "center" ? "0 auto 10px" : "0 0 10px 16px",
                  }} />
                )}
                <div style={{ position: "relative", overflow: "hidden",
                  WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
                  maskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
                }}>
                  <style>{`@keyframes brands-marquee-preview{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
                  <div style={{ display: "flex", animation: `brands-marquee-preview ${bmSpeed * 0.4}s linear infinite` }}>
                    {repeated.map((logo, idx) => (
                      <div key={idx} style={{ flexShrink: 0, width: 70, height: 32, marginRight: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {logo.url
                          ? <img src={resolveImgUrl(logo.url)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", filter: "grayscale(1)", opacity: 0.5 }} />
                          : <div style={{ width: 50, height: 18, background: c.secondary, borderRadius: 3 }} />
                        }
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          } else if (section.type === "search") {
            const sbBg = section.bgColor || "#fff9f5";
            content = (
              <div style={{ background: sbBg, padding: "16px 14px" }}>
                {section.heading && (
                  <div style={{ fontWeight: 700, fontSize: 12, textAlign: "center", marginBottom: 8, color: c.text }}>
                    {section.heading}
                  </div>
                )}
                <div style={{ maxWidth: 480, margin: "0 auto" }}>
                <div style={{
                  border: `2px solid ${c.primary}`,
                  borderRadius: 12, padding: "9px 12px", background: "#fff",
                  boxShadow: `0 2px 8px ${c.primary}18`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c.primary}
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <span style={{ fontSize: 11, color: "#aaa", flex: 1 }}>
                      {section.title || "¿Qué producto estás buscando?"}
                    </span>
                  </div>
                  {section.showCategoryChips !== false && (
                    <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
                      {["Categoría 1", "Categoría 2", "Categoría 3"].map(cat => (
                        <span key={cat} style={{
                          padding: "3px 9px", borderRadius: 20, fontSize: 9.5, fontWeight: 600,
                          background: `${c.primary}18`, color: c.primary,
                        }}>{cat}</span>
                      ))}
                    </div>
                  )}
                </div>
                </div>
              </div>
            );
          }
          if (!content) return null;
          return (
            <PreviewSectionWrapper
              key={i}
              type={section.type}
              editingType={editingType}
              onEdit={() => onEditSection?.(section.type)}
              dragSrc={pDragSrc}
              dragOver={pDragOver}
              onDragStart={pHandleDragStart}
              onDragOver={pHandleDragOver}
              onDrop={pHandleDrop}
              onDragEnd={pHandleDragEnd}
            >
              {content}
            </PreviewSectionWrapper>
          );
        })}

      {/* Footer */}
      {(() => {
        const mut = `${footer.textColor}99`;
        const div = `${footer.textColor}20`;
        const contactSec = (homeSections || []).find(s => s.type === "contact") || {};
        const fEmail = footer.email || contactSec.email || "";
        const fPhone = footer.phone || contactSec.phone || "";
        const fAddress = footer.address || contactSec.address || "";
        const fLogo = logos?.footer || logos?.desktop;
        const cols = [true, true, footer.showNav !== false, true].filter(Boolean).length;
        const fIconColor = footer.iconColor || c.accent;
        const fSocialColor = footer.socialIconColor || footer.textColor;
        const socialPlatforms = [
          { key: "instagram", icon: "bi bi-instagram", show: footer.showInstagram !== false && footer.instagram },
          { key: "facebook", icon: "bi bi-facebook", show: footer.showFacebook !== false && footer.facebook },
          { key: "tiktok", icon: "bi bi-tiktok", show: footer.showTiktok !== false && footer.tiktok },
          { key: "twitter", icon: "bi bi-twitter-x", show: footer.showTwitter !== false && footer.twitter },
        ];
        const visibleSocial = socialPlatforms.filter(p => p.show);
        const socialToShow = visibleSocial.length > 0 ? visibleSocial : socialPlatforms;

        const PreviewLogoEl = () => fLogo
          ? <img src={resolveImgUrl(fLogo)} alt="" style={{ height: 24, maxWidth: 80, objectFit: "contain", marginBottom: 8, display: "block" }} />
          : <div style={{ height: 16, width: 56, background: "rgba(255,255,255,0.2)", borderRadius: 3, marginBottom: 8 }} />;

        const PreviewContactCol = () => (
          <div>
            <div style={{ color: footer.textColor, fontWeight: 600, fontSize: 9, marginBottom: 7 }}>Contacto</div>
            {[fEmail && { icon: "bi-envelope", text: fEmail }, fPhone && { icon: "bi-telephone", text: fPhone }, fAddress && { icon: "bi-geo-alt", text: fAddress }]
              .filter(Boolean).map((item, ii) => (
                <div key={ii} style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4, color: mut, fontSize: 9 }}>
                  <i className={`bi ${item.icon}`} style={{ color: fIconColor, fontSize: 10, flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.text}</span>
                </div>
              ))}
            {!fEmail && !fPhone && !fAddress && [1, 2].map(n => (
              <div key={n} style={{ height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 2, marginBottom: 4 }} />
            ))}
          </div>
        );

        const PreviewSocialIcons = () => (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {socialToShow.map(({ key, icon }) => (
              <div key={key} style={{ width: 18, height: 18, borderRadius: "50%", background: `${fSocialColor}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={icon} style={{ color: fSocialColor, fontSize: 10 }} />
              </div>
            ))}
          </div>
        );

        return (
          <div style={{ background: footer.bgColor, color: footer.textColor, fontFamily: brand.font, padding: "18px 16px 12px" }}>
            <div style={{ maxWidth: "94%", margin: "0 auto" }}>
              {isMobile ? (
                /* Mobile: columna centrada */
                <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", textAlign: "center" }}>
                  {footer.showLogo !== false && <PreviewLogoEl />}
                  {footer.description && <p style={{ color: mut, fontSize: 9, lineHeight: 1.5, margin: 0, maxWidth: 200 }}>{footer.description}</p>}
                  {(fEmail || fPhone || fAddress) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {[fEmail && { icon: "bi-envelope", text: fEmail }, fPhone && { icon: "bi-telephone", text: fPhone }, fAddress && { icon: "bi-geo-alt", text: fAddress }]
                        .filter(Boolean).map((item, ii) => (
                          <div key={ii} style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center", color: mut, fontSize: 9 }}>
                            <i className={`bi ${item.icon}`} style={{ color: fIconColor, fontSize: 10, flexShrink: 0 }} />
                            <span>{item.text}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  {footer.showNav !== false && (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                      {["Inicio", "Productos", "Mis pedidos", "Iniciar sesión"].map(l => (
                        <span key={l} style={{ color: mut, fontSize: 9 }}>{l}</span>
                      ))}
                    </div>
                  )}
                  {footer.showPaymentMethods !== false && (
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
                      {["MP", "Transf.", "Efectivo"].map(m => (
                        <div key={m} style={{ padding: "2px 5px", border: `1px solid ${footer.textColor}30`, borderRadius: 3, fontSize: 8, color: mut }}>{m}</div>
                      ))}
                    </div>
                  )}
                  {footer.showSocial !== false && <PreviewSocialIcons />}
                </div>
              ) : (
                /* Desktop: grid de columnas */
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap: 12 }}>
                  {/* Col 1: Logo + desc */}
                  <div>
                    {footer.showLogo !== false && <PreviewLogoEl />}
                    {footer.description
                      ? <p style={{ color: mut, fontSize: 9, lineHeight: 1.5, margin: 0 }}>{footer.description}</p>
                      : <><div style={{ height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 2, marginBottom: 4 }} />
                        <div style={{ height: 6, width: "70%", background: "rgba(255,255,255,0.1)", borderRadius: 2 }} /></>
                    }
                  </div>
                  {/* Col 2: Contacto */}
                  <PreviewContactCol />
                  {/* Col 3: Navegación (opcional) */}
                  {footer.showNav !== false && (
                    <div>
                      <div style={{ color: footer.textColor, fontWeight: 600, fontSize: 9, marginBottom: 7 }}>Navegación</div>
                      {["Inicio", "Productos", "Mis pedidos", "Iniciar sesión"].map(l => (
                        <div key={l} style={{ color: mut, fontSize: 9, marginBottom: 4 }}>{l}</div>
                      ))}
                    </div>
                  )}
                  {/* Col 4: Medios de pago + redes */}
                  <div>
                    {footer.showPaymentMethods !== false && (
                      <>
                        <div style={{ color: footer.textColor, fontWeight: 600, fontSize: 9, marginBottom: 7 }}>Medios de pago</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 10 }}>
                          {["MP", "Transf.", "Efectivo"].map(m => (
                            <div key={m} style={{ padding: "2px 5px", border: `1px solid ${footer.textColor}30`, borderRadius: 3, fontSize: 8, color: mut }}>{m}</div>
                          ))}
                        </div>
                      </>
                    )}
                    {footer.showSocial !== false && (
                      <>
                        <div style={{ color: footer.textColor, fontWeight: 600, fontSize: 9, marginBottom: 7 }}>Redes</div>
                        <PreviewSocialIcons />
                      </>
                    )}
                  </div>
                </div>
              )}
              <div style={{ marginTop: 12, paddingTop: 8, borderTop: `1px solid ${div}`, textAlign: "center", color: mut, fontSize: 8 }}>
                © {new Date().getFullYear()} · Todos los derechos reservados
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function FaqEditor({ faqPage, onChange }) {
  const page = faqPage || {};
  const set = (k, v) => onChange({ ...page, [k]: v });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar página</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Habilita /faq y el link en el pie de página</div>
        </div>
        <Toggle on={page.visible === true} onChange={v => set("visible", v)} />
      </div>
      <Field label="Título">
        <TInput value={page.title || ""} onChange={v => set("title", v)} placeholder="Preguntas frecuentes" />
        <TextStyleRow label="Título" style={page.titleStyle}
          onChange={v => set("titleStyle", v)} />
      </Field>
      <Field label="Subtítulo (opcional)">
        <TInput value={page.subtitle || ""} onChange={v => set("subtitle", v)} placeholder="Resolvemos tus dudas más comunes" />
      </Field>
      <TitleAlignRow section={page} onChange={onChange} />
      <ColorRow label="Color de fondo" value={page.bgColor || "#ffffff"}
        onChange={v => set("bgColor", v)} />
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: `1px solid ${T.bdr}`
      }}>
        <div>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 500 }}>Mostrar filtros de palabras clave</div>
          <div style={{ fontSize: 11, color: T.mut, marginTop: 2 }}>Badges para filtrar preguntas por palabra clave</div>
        </div>
        <Toggle on={page.showSearch !== false} onChange={v => set("showSearch", v)} />
      </div>
      <ColorRow label="Color de la pregunta" value={page.questionColor || "#1a1a3a"}
        onChange={v => set("questionColor", v)} />
      <ColorRow label="Color de la respuesta" value={page.answerColor || "#1a1a3a"}
        onChange={v => set("answerColor", v)} />
      <div style={{
        background: T.blueBg, border: `1px solid ${T.blue}30`, borderRadius: T.rs,
        padding: "10px 12px", fontSize: 12, color: T.sub, lineHeight: 1.5,
      }}>
        Las preguntas, respuestas e íconos se cargan desde <strong>Preguntas frecuentes</strong> en el menú del panel.
      </div>
    </div>
  );
}

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV = [
  {
    group: "Imagen de tu marca", items: [
      { id: "logos", label: "Logos", icon: "🖼" },
      { id: "colores", label: "Colores de tu marca", icon: "🎨" },
      { id: "fuente", label: "Tipo de letra", icon: "Aa" },
      { id: "login", label: "Inicio de sesión", icon: "🔐" },
    ]
  },
  {
    group: "Configuraciones avanzadas", items: [
      { id: "header", label: "Encabezado", icon: "↑" },
      { id: "home", label: "Página de inicio", icon: "🏠" },
      { id: "listing", label: "Listado de productos", icon: "⊞" },
      { id: "detail", label: "Detalle de producto", icon: "📦" },
      { id: "cart", label: "Carrito de compras", icon: "🛒" },
      { id: "footer", label: "Pie de página", icon: "↓" },
      { id: "widgets", label: "Botón de WhatsApp", icon: "💬" },
      { id: "faq", label: "Preguntas frecuentes", icon: "❓" },
    ]
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────
export const ConfigTemplate4 = () => {
  const { config, refreshConfig, loadingConfig } = useClient();

  const [activeSection, setActiveSection] = useState("colores");
  const [device, setDevice] = useState("desktop");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [saveState, setSaveState] = useState("idle");
  const [previewData, setPreviewData] = useState({ products: [], categorias: [], faqs: [] });
  const [homeEditingType, setHomeEditingType] = useState(null);
  const [logos, setLogos] = useState({ desktop: null, mobile: null, footer: null, dark: null });
  const [favicon, setFavicon] = useState("");
  const [loginBg, setLoginBg] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [savedPrimary, setSavedPrimary] = useState(DEFAULT_CONFIG.brand.colors.primary);

  useEffect(() => { loadGoogleFont(cfg.brand?.font); }, [cfg.brand?.font]);

  useEffect(() => {
    if (!config?.id) return;
    Promise.all([
      api.admin.catalogo.getProductos().catch(() => ({ data: [] })),
      api.admin.catalogo.getCategorias().catch(() => ({ data: [] })),
      api.faq.admin.getAll().catch(() => ({ data: [] })),
    ]).then(([prodsRes, catsRes, faqsRes]) => {
      const prods = Array.isArray(prodsRes?.data) ? prodsRes.data : [];
      const cats = Array.isArray(catsRes?.data) ? catsRes.data : [];
      const faqs = Array.isArray(faqsRes?.data) ? faqsRes.data : [];
      setPreviewData({
        products: prods.map(p => ({
          ...p,
          // Admin API returns p.precios[]; derive a single precio for preview display
          precio: p.precios?.find(pr => pr.precio > 0)?.precio ?? 0,
        })),
        categorias: cats,
        faqs: faqs.filter(f => f.activo),
      });
    });
  }, [config?.id]);

  const logosRef = useRef(false);
  useEffect(() => {
    if (!config?.id || logosRef.current) return;
    logosRef.current = true;
    setLogos({
      desktop: config.logo?.desktop || null,
      mobile: config.logo?.mobile || null,
      footer: config.logo?.footer || null,
      dark: config.logo?.dark || null,
      login: config.logo?.login || null,
    });
    setFavicon(config.favicon || "");
    setLoginBg(config.loginBg || "");
    setWhatsappNumber(config.contact?.whatsapp || "");
  }, [config?.id]);

  const syncedRef = useRef(false);
  useEffect(() => {
    if (loadingConfig || syncedRef.current || !config?.id) return;
    syncedRef.current = true;

    const saved = config.uiConfig?.t4;
    const initPrimary = saved?.brand?.colors?.primary || config.theme?.primaryColor || DEFAULT_CONFIG.brand.colors.primary;
    setSavedPrimary(initPrimary);
    if (saved) {
      setCfg(prev => ({
        brand: { ...DEFAULT_CONFIG.brand, ...saved.brand, colors: { ...DEFAULT_CONFIG.brand.colors, ...(saved.brand?.colors || {}) } },
        header: { ...DEFAULT_CONFIG.header, ...saved.header },
        homeSections: (() => {
          const base = saved.homeSections?.length ? saved.homeSections : DEFAULT_CONFIG.homeSections;
          const existingTypes = new Set(base.map(s => s.type));
          const missing = DEFAULT_CONFIG.homeSections.filter(s => !existingTypes.has(s.type));
          const maxOrder = base.reduce((m, s) => Math.max(m, s.order ?? 0), 0);
          return [...base, ...missing.map((s, i) => ({ ...s, order: maxOrder + 1 + i }))];
        })(),
        productList: { ...DEFAULT_CONFIG.productList, ...saved.productList },
        productDetail: { ...DEFAULT_CONFIG.productDetail, ...saved.productDetail },
        cart: { ...DEFAULT_CONFIG.cart, ...saved.cart },
        footer: { ...DEFAULT_CONFIG.footer, ...saved.footer },
        floatingWhatsapp: { ...DEFAULT_CONFIG.floatingWhatsapp, ...saved.floatingWhatsapp },
        faqPage: { ...DEFAULT_CONFIG.faqPage, ...saved.faqPage },
      }));
    } else {
      const ui = config.uiConfig || {};
      // Migrar colores ya guardados de la config antigua
      if (config.theme) {
        setCfg(prev => ({
          ...prev,
          brand: {
            ...prev.brand, colors: {
              primary: config.theme.primaryColor || prev.brand.colors.primary,
              accent: config.theme.accentColor || prev.brand.colors.accent,
              background: config.theme.backgroundColor || prev.brand.colors.background,
              secondary: config.theme.secondaryColor || prev.brand.colors.secondary,
              text: config.theme.textColor || prev.brand.colors.text,
              primaryText: config.theme.primaryTextColor || prev.brand.colors.primaryText,
            }
          },
        }));
      }
    }
  }, [loadingConfig, config]);

  const setSubCfg = useCallback((key, val) => {
    setCfg(prev => ({ ...prev, [key]: val }));
  }, []);

  const handleSave = async () => {
    if (saveState !== "idle") return;
    setSaveState("saving");
    try {
      const uiConfig = { ...(config?.uiConfig || {}), t4: cfg };
      const res = await api.tienda.updateConfig({
        color_primary: cfg.brand.colors.primary,
        color_secondary: cfg.brand.colors.secondary,
        color_accent: cfg.brand.colors.accent,
        color_background: cfg.brand.colors.background,
        color_text: cfg.brand.colors.text,
        color_primary_text: cfg.brand.colors.primaryText,
        ui_config: JSON.stringify(uiConfig),
        logo_desktop: logos.desktop || "",
        logo_mobile: logos.mobile || "",
        logo_footer: logos.footer || "",
        logo_dark: logos.dark || "",
        logo_login: logos.login || "",
        favicon: favicon || "",
        login_bg: loginBg || "",
        instagram: cfg.footer.instagram || "",
        facebook: cfg.footer.facebook || "",
        tiktok: cfg.footer.tiktok || "",
        twitter: cfg.footer.twitter || "",
        whatsapp: whatsappNumber || "",
      });
      if (res.status === "ok") {
        setSavedPrimary(cfg.brand.colors.primary);
        await refreshConfig();
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1800);
      } else {
        message.error(res.error || "Error al guardar");
        setSaveState("idle");
      }
    } catch {
      message.error("Error de conexión");
      setSaveState("idle");
    }
  };

  const renderEditor = () => {
    switch (activeSection) {
      case "logos": return <LogosEditor logos={logos} onChange={setLogos} />;
      case "login": return <LoginEditor favicon={favicon} loginBg={loginBg} onFaviconChange={setFavicon} onLoginBgChange={setLoginBg} />;
      case "colores": return <ColoresEditor brand={cfg.brand} onChange={v => setSubCfg("brand", v)} />;
      case "fuente": return <TipoLetraEditor brand={cfg.brand} onChange={v => setSubCfg("brand", v)} />;
      case "header": return <EncabezadoEditor header={cfg.header} onChange={v => setSubCfg("header", v)} />;
      case "home": return <PaginaInicioEditor sections={cfg.homeSections} onChange={v => setSubCfg("homeSections", v)} editingType={homeEditingType} setEditingType={setHomeEditingType} />;
      case "listing": return <ListadoEditor productList={cfg.productList} onChange={v => setSubCfg("productList", v)} />;
      case "detail": return <DetalleEditor productDetail={cfg.productDetail} onChange={v => setSubCfg("productDetail", v)} />;
      case "cart": return <CarritoEditor cart={cfg.cart} onChange={v => setSubCfg("cart", v)} />;
      case "footer": return <PieEditor footer={cfg.footer} onChange={v => setSubCfg("footer", v)} />;
      case "faq": return <FaqEditor faqPage={cfg.faqPage} onChange={v => setSubCfg("faqPage", v)} />;
      case "widgets": return <WidgetsEditor data={cfg.floatingWhatsapp} onChange={v => setSubCfg("floatingWhatsapp", v)} whatsappNumber={whatsappNumber} onChangeNumber={setWhatsappNumber} />;
      default: return null;
    }
  };

  const activeMeta = NAV.flatMap(g => g.items).find(i => i.id === activeSection);

  if (loadingConfig) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <Spin size="large" tip="Cargando configuración..." />
      </div>
    );
  }

  const allNavItems = NAV.flatMap(g => g.items);

  const SaveButton = () => (
    <button onClick={handleSave}
      style={{
        padding: "8px 20px", borderRadius: 9, border: "none",
        background: saveState === "saved" ? "#059669" : T.blue, color: "white",
        fontSize: 13, fontWeight: 600, cursor: "pointer",
        transition: "background 0.25s", boxShadow: "0 2px 10px rgba(37,88,217,0.2)",
        display: "flex", alignItems: "center", gap: 7, flexShrink: 0,
      }}>
      {saveState === "saving" && (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.7s linear infinite" }}>
          <circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <path d="M6.5 1.5 A5 5 0 0 1 11.5 6.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      {saveState === "saved" ? "✓ Guardado" : "Guardar"}
    </button>
  );

  return (
    <div style={{
      margin: isMobile ? -16 : -24,
      height: "calc(100vh - 56px)", display: "flex",
      flexDirection: isMobile ? "column" : "row",
      overflow: "hidden", fontFamily: "system-ui,sans-serif", background: "#eaecf4",
      '--store-primary': savedPrimary,
      '--store-primary-bg': savedPrimary + '18'
    }}>

      {isMobile ? (
        /* ── Mobile layout: nav horizontal + editor ── */
        <>
          {/* Nav horizontal scrollable */}
          <div style={{
            flexShrink: 0, background: T.surf, borderBottom: `1px solid ${T.bdr}`,
            display: "flex", alignItems: "center", overflowX: "auto",
            gap: 2, padding: "0 8px",
          }}>
            {allNavItems.map(item => {
              const active = activeSection === item.id;
              return (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  style={{
                    flexShrink: 0, display: "flex", alignItems: "center", gap: 6,
                    padding: "12px 12px", border: "none", background: "transparent",
                    color: active ? T.blue : T.sub, cursor: "pointer", fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    borderBottom: `2.5px solid ${active ? T.blue : "transparent"}`,
                    outline: "none", whiteSpace: "nowrap", transition: "color 0.12s",
                  }}>
                  <span style={{ fontSize: 14 }}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Editor panel */}
          <div style={{ flex: 1, background: "#fff", overflowY: "auto", padding: "16px 16px" }}>
            {renderEditor()}
          </div>

          {/* Save bar */}
          <div style={{
            flexShrink: 0, background: T.surf, borderTop: `1px solid ${T.bdr}`,
            padding: "10px 16px", display: "flex", justifyContent: "flex-end",
          }}>
            <SaveButton />
          </div>
        </>
      ) : (
        /* ── Desktop layout: 3 columnas ── */
        <>
          {/* Sidebar */}
          <div style={{
            width: 220, flexShrink: 0, background: T.surf, borderRight: `1px solid ${T.bdr}`,
            display: "flex", flexDirection: "column", overflowY: "auto"
          }}>
            <div style={{ padding: "16px 14px 8px", borderBottom: `1px solid ${T.bdr}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.mut, letterSpacing: .6, textTransform: "uppercase" }}>
                Diseño de tienda
              </div>
            </div>
            {NAV.map(group => (
              <div key={group.group} style={{ paddingBottom: 4 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: T.mut, letterSpacing: .5,
                  textTransform: "uppercase", padding: "14px 16px 6px"
                }}>
                  {group.group}
                </div>
                {group.items.map(item => {
                  const active = activeSection === item.id;
                  return (
                    <button key={item.id} onClick={() => setActiveSection(item.id)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                        padding: "9px 16px", border: "none", background: active ? T.blueBg : "transparent",
                        color: active ? T.blue : T.txt, cursor: "pointer", fontSize: 13,
                        fontWeight: active ? 600 : 400, textAlign: "left",
                        borderLeft: `3px solid ${active ? T.blue : "transparent"}`,
                        transition: "all 0.12s"
                      }}>
                      <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Editor panel */}
          <div style={{
            width: 340, flexShrink: 0, background: "#fff", borderRight: `1px solid ${T.bdr}`,
            display: "flex", flexDirection: "column", overflow: "hidden"
          }}>
            <div style={{
              padding: "14px 18px", borderBottom: `1px solid ${T.bdr}`, flexShrink: 0,
              display: "flex", alignItems: "center", gap: 10
            }}>
              <span style={{ fontSize: 18 }}>{activeMeta?.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.txt }}>{activeMeta?.label}</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "18px 18px" }}>
              {renderEditor()}
            </div>
          </div>

          {/* Preview */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{
              height: 52, flexShrink: 0, background: T.surf, borderBottom: `1px solid ${T.bdr}`,
              padding: "0 16px", display: "flex", alignItems: "center", gap: 10
            }}>
              <div style={{ flex: 1 }} />
              <div style={{ display: "flex", gap: 4 }}>
                {[{ id: "desktop", icon: "🖥" }, { id: "mobile", icon: "📱" }].map(({ id, icon }) => (
                  <button key={id} onClick={() => setDevice(id)}
                    style={{
                      width: 30, height: 30, borderRadius: 7, fontSize: 13, cursor: "pointer",
                      border: `1.5px solid ${device === id ? T.blue : T.bdr}`,
                      background: device === id ? T.blueBg : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", outline: "none",
                      transition: "all 0.15s"
                    }}>{icon}</button>
                ))}
              </div>
              <SaveButton />
            </div>

            <div style={{
              flex: 1, overflowY: "auto", display: "flex", justifyContent: "center",
              padding: "20px",
              backgroundImage: "radial-gradient(#bfc6d6 1px,transparent 1px)",
              backgroundSize: "20px 20px"
            }}>
              <div style={{
                width: device === "desktop" ? "100%" : "390px", maxWidth: 620, flexShrink: 0,
                transition: "width 0.3s cubic-bezier(.25,.1,.25,1)"
              }}>
                <div style={{
                  background: "#1e2230", borderRadius: "10px 10px 0 0",
                  padding: "10px 14px", display: "flex", alignItems: "center", gap: 10
                }}>
                  <div style={{ display: "flex", gap: 5.5 }}>
                    {["#ff5f57", "#febc2e", "#28c840"].map(c => (
                      <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                  </div>
                  <div style={{
                    flex: 1, background: "#2e3347", borderRadius: 5, padding: "4px 10px",
                    fontSize: 10.5, color: "#6b7a8f", textAlign: "center"
                  }}>
                    🔒 {config?.id ? `${config.id}.ip-web.com.ar` : "tutienda.com.ar"}
                  </div>
                  <div style={{ width: 16 }} />
                </div>
                <div style={{
                  borderRadius: "0 0 10px 10px", overflow: "hidden",
                  border: `1px solid ${T.bdr}`, borderTop: "none",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.09)"
                }}>
                  <T4Preview
                    cfg={cfg}
                    whatsappNumber={whatsappNumber}
                    storeName={config?.displayName || config?.name || "Mi Tienda"}
                    products={previewData.products}
                    categorias={previewData.categorias}
                    faqs={previewData.faqs}
                    logos={logos}
                    device={device}
                    activeSection={activeSection}
                    editingType={homeEditingType}
                    onEditSection={type => { setActiveSection("home"); setHomeEditingType(type); }}
                    onReorderSections={(srcType, destType) => {
                      const secs = cfg.homeSections;
                      const srcOrder = secs.find(s => s.type === srcType)?.order;
                      const targetOrder = secs.find(s => s.type === destType)?.order;
                      if (srcOrder == null || targetOrder == null) return;
                      setSubCfg("homeSections", secs.map(s => {
                        if (s.type === srcType) return { ...s, order: targetOrder };
                        if (s.type === destType) return { ...s, order: srcOrder };
                        return s;
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};
